import React, { useState, useEffect, useMemo } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Paper,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Checkbox,
    Radio,
    RadioGroup,
    Stack,
    IconButton,
    Alert,
    Autocomplete,
    Popper,
    ClickAwayListener,
    FormHelperText,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { missingDaysAfterTypeFlag } from "../utils/daysAfterType";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { getAttributeSuggestions } from "../utils/attributeSuggestions";
import {
    getValidationSuggestions,
    getValidationSuggestionsForType,
    getValidationErrorMessageSuggestions,
} from "../utils/validationSuggestions";
import { getEffectivePdfLocationForExtraBinding } from "../utils/pdfFieldPlacement";
import { normalizeEditableAnnotation } from "../utils/formParser";
import { getDefaultRoleLabels, buildSignaturesTemplate } from "../utils/signatureUtils";

// Utility to convert a string to camelCase
function toCamelCase(str) {
    return str
        .replace(/[-_\s]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ""))
        .replace(/^(.)/, (m) => m.toLowerCase());
}

const FIELD_TYPES = [
    "input",
    "datetime",
    "currency",
    "textarea",
    "checkbox",
    "checkboxWithOther",
    "radio",
    "radioWithOther",
    "decision",
    "decisionWithOther",
    "inlineInput",
];

/** Same as App `ATTACH_COMPATIBLE_FIELD_TYPES`: attach placed box + Extra PDF UI for every section field type. */
const ATTACH_COMPATIBLE_TYPES = new Set(FIELD_TYPES);

const DEFAULT_SIGNER_ROLE_LABELS = getDefaultRoleLabels();

function fieldHasPrimaryPdfPlacement(f) {
    const p = f?.metadata?.pdfLocation;
    if (!p || typeof p !== "object") return false;
    return p.page != null;
}


const EditFieldDialog = ({
    open,
    field,
    sections,
    fields = [],
    onClose,
    onSave,
    onDelete,
    mode = "json",
    variant = "dialog",
    isPurchaseContract = false,
    signatures = {},
    initials = null,
    additionalFields = [],
    signerAttributeBindings = [],
    initialerAttributeBindings = [],
    subsequentSigningBindings = [],
    subsequentSigning = {},
    signatureAnnotations = [],
    initialsAnnotations = [],
    onFieldPdfBindingSync = null,
    /** If set (e.g. `['input','checkbox']`), Field Type select only lists these (plus current type if missing). */
    fieldTypeFilter = null,
    /** JSON PDF editor: (sourceId, targetId) => move pdfLocation from source field onto target and delete source. */
    onAttachPlacedAnnotation = null,
    /** When editing subsequent fields, reserve attribute names against the main form too. */
    mainFormFieldsForUniqueness = [],
    /** Form-level daysAfter settings ({ mec, bad, bs, coe, dbc }) — drives the days-after Type dropdown options. */
    daysAfterSettings = {},
}) => {
    const [localField, setLocalField] = useState(null);
    const [attachToFieldId, setAttachToFieldId] = useState("");
    const [localOptions, setLocalOptions] = useState([]);
    const [attributeError, setAttributeError] = useState(null);
    const [otherInputError, setOtherInputError] = useState(null);
    const [optionErrors, setOptionErrors] = useState({});
    const [attributeSuggestions, setAttributeSuggestions] = useState([]);
    const [validationErrorMessageSuggestions, setValidationErrorMessageSuggestions] = useState([]);
    const [validationSuggestionsForType, setValidationSuggestionsForType] = useState({ patterns: [], validation: {}, suggestedErrorMessage: "" });
    const [usedPatternsFromTemplates, setUsedPatternsFromTemplates] = useState([]);
    const [usedPatternsByFormat, setUsedPatternsByFormat] = useState({});
    const [patternSuggestionsAnchorEl, setPatternSuggestionsAnchorEl] = useState(null);
    /** standard | additionalField | personaData | initialsData — section field extra PDF */
    const [extraPdfBindingKind, setExtraPdfBindingKind] = useState("standard");
    /** Section/synthetic: attributePlacements when Persona is chosen in UI; signatureImage only for legacy linked sig-on-field or synthetic signature row (no UI toggle). */
    const [personaPdfSubtype, setPersonaPdfSubtype] = useState("attributePlacements");
    const [extraPdfPersonaCompound, setExtraPdfPersonaCompound] = useState("");
    /** Multi-select signer compounds (`roleKey\tsignerKey`) for Extra PDF → subsequent signing */
    const [syntheticSignerCompound, setSyntheticSignerCompound] = useState("");
    /**
     * "Also a signer field" toggle. Held separately from `metadata.signerKey` so
     * the section can stay open with nothing picked yet — an unpicked key is
     * omitted from metadata rather than published as "".
     */
    const [alsoSignerField, setAlsoSignerField] = useState(false);
    /** Synthetic / linked row: same three values as extraPdfBindingKind */
    const [pdfExtraOutputTarget, setPdfExtraOutputTarget] = useState("additionalField");
    const [pdfExtraTargetError, setPdfExtraTargetError] = useState(null);

    /** Role keys enabled in Form Settings (signer roles), sorted for stable UI */
    const signingRoleKeysForEditable = useMemo(
        () => Object.keys(signatures || {}).filter((k) => signatures[k]).sort(),
        [signatures],
    );

    const dealRoleValueForSigningRole = (roleKey) => {
        const v = signatures?.[roleKey]?.meviDealRole ?? roleKey;
        const s = String(v || "").trim();
        return s || roleKey;
    };

    const signerOptionsFlat = useMemo(() => {
        const opts = [];
        Object.entries(signatures || {}).forEach(([roleKey, rv]) => {
            const roleLabel = rv?.label || roleKey;
            (rv?.signatures || []).forEach((sig) => {
                if (!sig?.signerKey) return;
                opts.push({
                    compound: `${roleKey}\t${sig.signerKey}`,
                    roleKey,
                    signerKey: sig.signerKey,
                    label: `${roleLabel}: ${sig.signerKey}${sig.email ? ` (${sig.email})` : ""}`,
                });
            });
        });
        return opts;
    }, [signatures]);

    /**
     * Signer options for a SUBSEQUENT-SIGNING box.
     *
     * Resolved from subsequentSigningJSON alone — never from signaturesJSON. The two rosters are
     * independent: a form can be acknowledged by a role that does not sign it (RF401 acknowledges
     * on team.sell and carries no team.sell signer block at all), so borrowing the signer list
     * would offer signers the block does not have and hide the ones it does.
     *
     * A role ticked under Subsequent Signing Roles always has its one row, so the rows ARE the
     * options; the standard keys stand in only for a role somehow stored with none.
     */
    const subsequentSignerOptionsFlat = useMemo(() => {
        const template = buildSignaturesTemplate();
        const stored =
            subsequentSigning && typeof subsequentSigning === "object" && !Array.isArray(subsequentSigning)
                ? subsequentSigning
                : {};
        const opts = [];
        const seen = new Set();
        Object.entries(stored).forEach(([roleKey, block]) => {
            const roleLabel = block?.label || roleKey;
            const rows = Array.isArray(block?.signatures) ? block.signatures : [];
            const keys = rows.length > 0
                ? rows.map((r) => r?.signerKey).filter(Boolean)
                : (template[roleKey]?.signatures || []).map((sig) => sig?.signerKey).filter(Boolean);
            [...new Set(keys)].forEach((signerKey) => {
                const compound = `${roleKey}\t${signerKey}`;
                if (seen.has(compound)) return;
                seen.add(compound);
                const row = rows.find((r) => r?.signerKey === signerKey);
                opts.push({
                    compound,
                    roleKey,
                    signerKey,
                    label: `${roleLabel}: ${signerKey}${row?.email ? ` (${row.email})` : ""}`,
                });
            });
        });
        return opts;
    }, [subsequentSigning]);

    /**
     * Signer options for an INITIALS box.
     *
     * Initials resolve from initialsJSON, not signaturesJSON: a form can be initialled without
     * being signed, and prune removes signature slots with no tab — which can leave `signatures`
     * empty. Reading signerOptionsFlat here left the dropdown blank on exactly the forms that
     * are initials-only (F259), so a placed tab could not even show the signer it was bound to.
     *
     * Union of: rows on the stored initials role, signers already placed on the PDF, and the
     * standard signer keys of a ticked role that has no tabs yet — so a freshly ticked role can
     * be assigned its first box.
     */
    const initialsSignerOptionsFlat = useMemo(() => {
        const template = buildSignaturesTemplate();
        const stored = initials && typeof initials === "object" ? initials : {};
        const roleKeys = [
            ...Object.keys(stored),
            ...(initialsAnnotations || []).map((a) => a?.roleKey),
            ...Object.keys(signatures || {}),
        ].filter(Boolean);

        const opts = [];
        const seen = new Set();
        [...new Set(roleKeys)].forEach((roleKey) => {
            const roleLabel =
                stored[roleKey]?.label || signatures?.[roleKey]?.label || roleKey;
            const rows = stored[roleKey]?.initials ?? stored[roleKey]?.signatures ?? [];
            // Real signers only: rows on the role plus whatever is placed on the PDF. The standard
            // keys are offered ONLY when the role has neither — a role just ticked under Initials
            // Roles, which needs something to assign its first box to. Otherwise a form with two
            // initialled buyers would list Buyer3..Buyer6 as well.
            const real = [
                ...rows.map((r) => r?.signerKey),
                ...(initialsAnnotations || []).filter((a) => a?.roleKey === roleKey).map((a) => a?.signerKey),
                ...(signatures?.[roleKey]?.signatures || []).map((sig) => sig?.signerKey),
            ].filter(Boolean);
            const keys = real.length > 0
                ? real
                : (template[roleKey]?.signatures || []).map((sig) => sig?.signerKey).filter(Boolean);
            [...new Set(keys)].forEach((signerKey) => {
                const compound = `${roleKey}\t${signerKey}`;
                if (seen.has(compound)) return;
                seen.add(compound);
                const email =
                    rows.find((r) => r?.signerKey === signerKey)?.email ||
                    (signatures?.[roleKey]?.signatures || []).find((sig) => sig?.signerKey === signerKey)?.email ||
                    "";
                opts.push({
                    compound,
                    roleKey,
                    signerKey,
                    label: `${roleLabel}: ${signerKey}${email ? ` (${email})` : ""}`,
                });
            });
        });
        return opts;
    }, [initials, initialsAnnotations, signatures]);

    /** Distinct signer keys across all roles, for `metadata.signerKey` ("Also a signer field"). */
    const signerKeyOptions = useMemo(() => {
        const byKey = new Map();
        signerOptionsFlat.forEach(({ signerKey, roleKey }) => {
            const roleLabel = signatures?.[roleKey]?.label || roleKey;
            const entry = byKey.get(signerKey) || { signerKey, roleLabels: [] };
            if (!entry.roleLabels.includes(roleLabel)) entry.roleLabels.push(roleLabel);
            byKey.set(signerKey, entry);
        });
        return [...byKey.values()].sort((a, b) => a.signerKey.localeCompare(b.signerKey));
    }, [signerOptionsFlat, signatures]);

    /**
     * Signer key the attribute name is prefixed with, e.g.
     * `Buyer2_legalInformation_fullName` → `Buyer2`. Longest match wins so
     * `Buyer2` beats `Buyer` when both are signer keys. "" when none match.
     */
    const signerKeyFromAttributePrefix = useMemo(() => {
        const attr = String(localField?.attributeName || "").trim().toLowerCase();
        if (!attr) return "";
        const match = [...signerKeyOptions]
            .map((o) => o.signerKey)
            .sort((a, b) => b.length - a.length)
            .find((k) => attr.startsWith(String(k).toLowerCase()));
        return match || "";
    }, [localField?.attributeName, signerKeyOptions]);

    const fieldTypeSelectOptions = useMemo(() => {
        let list = fieldTypeFilter?.length
            ? FIELD_TYPES.filter((t) => fieldTypeFilter.includes(t))
            : FIELD_TYPES;
        if (localField?.type && !list.includes(localField.type)) {
            list = [...list, localField.type];
        }
        return list;
    }, [fieldTypeFilter, localField?.type]);

    const attachCandidates = useMemo(() => {
        if (!onAttachPlacedAnnotation || !localField || mode !== "json" || localField.pdfEditorSyntheticKind) {
            return [];
        }
        if (!fieldHasPrimaryPdfPlacement(localField)) return [];
        if (!ATTACH_COMPATIBLE_TYPES.has(localField.type)) return [];
        if (localField.isOverflow) return [];
        return (fields || []).filter(
            (f) =>
                f.id !== localField.id &&
                f.attributeName &&
                f.type === localField.type &&
                !f.isOverflow &&
                !fieldHasPrimaryPdfPlacement(f),
        );
    }, [fields, localField, mode, onAttachPlacedAnnotation]);

    /** Show attach slot to the right of Field Type (even when there are zero target fields). */
    const showAttachPlacedRow = useMemo(() => {
        if (!onAttachPlacedAnnotation || !localField || mode !== "json" || localField.pdfEditorSyntheticKind) {
            return false;
        }
        if (!fieldHasPrimaryPdfPlacement(localField)) return false;
        if (!ATTACH_COMPATIBLE_TYPES.has(localField.type)) return false;
        if (localField.isOverflow) return false;
        return true;
    }, [localField, mode, onAttachPlacedAnnotation]);

    useEffect(() => {
        setAttachToFieldId("");
    }, [field?.id, open]);

    useEffect(() => {
        if (field) {
            let initializedField = { ...field };
            const defaultRules = {
                notes: "",
                sectionNumber: "",
                sectionName: "",
                sectionDescription: "",
                sectionNotes: "",
            };
            
            // Ensure datetime fields have format in metadata.fieldData
            if (field.type === "datetime") {
                if (!initializedField.metadata) {
                    initializedField.metadata = {};
                }
                if (!initializedField.metadata.fieldData) {
                    initializedField.metadata.fieldData = {};
                }
                // Ensure format is always present
                if (!initializedField.metadata.fieldData.format) {
                    initializedField.metadata.fieldData.format = "01/02/2006";
                }
            }

            initializedField.rules = {
                ...defaultRules,
                ...(initializedField.rules || {}),
            };
            if (initializedField.validation?.notes && !initializedField.rules.notes) {
                initializedField.rules.notes = initializedField.validation.notes;
            }
            if (initializedField.validation?.notes) {
                const { notes, ...restValidation } = initializedField.validation;
                initializedField.validation = restValidation;
            }
            initializedField.annotationMultiline = initializedField.annotationMultiline ?? 0;
            initializedField.isOverflow = initializedField.isOverflow ?? null;
            initializedField.editableAnnotation = normalizeEditableAnnotation(field.editableAnnotation);

            setLocalField(initializedField);
            setLocalOptions(initializedField.metadata?.options || []);
            const defaultOther = initializedField.type === "decisionWithOther"
                ? { decisionValue: "other", input: { attributeName: "otherInput", placeholder: "Please specify...", validation: {} } }
                : {
                    [initializedField.type === "radioWithOther" ? "radio" : "checkbox"]: { text: "Other", value: "other" },
                    input: { attributeName: "", placeholder: "Please specify...", validation: {} }
                };
            setLocalOther(initializedField.metadata?.other || defaultOther);
            setSyntheticSignerCompound(field.pdfExtraSignerSelectionCompound || "");
            if (field.pdfEditorSyntheticKind) {
                const t = field.pdfExtraOutputTarget;
                let out =
                    t === "additionalFieldsJSON"
                        ? "additionalField"
                        : t === "subsequentSigningJSON"
                          ? "subsequentSigning"
                          : t === "signaturesJSON"
                            ? "personaData"
                            : t === "signaturesJSON_signatureImage"
                              ? "personaData"
                              : t === "initialsJSON"
                                ? "initialsData"
                                : t === "initialsJSON_signatureImage"
                                  ? "initialsData"
                              : t === "additionalField" ||
                                  t === "personaData" ||
                                  t === "initialsData" ||
                                  t === "subsequentSigning" ||
                                  t === "standard"
                                ? t
                                : null;
                let sub = field.pdfPersonaSubtype;
                if (t === "signaturesJSON") sub = sub || "attributePlacements";
                if (t === "signaturesJSON_signatureImage") sub = "signatureImage";
                if (t === "initialsJSON") sub = sub || "attributePlacements";
                if (t === "initialsJSON_signatureImage") sub = "signatureImage";
                if (!out) {
                    out =
                        field.pdfEditorSyntheticKind === "signerBinding" ||
                        field.pdfEditorSyntheticKind === "signatureAnnotation"
                            ? "personaData"
                            : field.pdfEditorSyntheticKind === "initialsAnnotation"
                              ? "initialsData"
                            : field.pdfEditorSyntheticKind === "subsequentSigning"
                              ? "subsequentSigning"
                              : "additionalField";
                    sub =
                        field.pdfEditorSyntheticKind === "signatureAnnotation" ||
                        field.pdfEditorSyntheticKind === "initialsAnnotation"
                            ? "signatureImage"
                            : "attributePlacements";
                }
                if (out === "personaData" && !sub) {
                    sub =
                        field.pdfEditorSyntheticKind === "signatureAnnotation"
                            ? "signatureImage"
                            : "attributePlacements";
                }
                if (out === "initialsData" && !sub) {
                    sub =
                        field.pdfEditorSyntheticKind === "initialsAnnotation"
                            ? "signatureImage"
                            : "attributePlacements";
                }
                setPdfExtraOutputTarget(out);
                setPersonaPdfSubtype(sub || "attributePlacements");

            }
            setAlsoSignerField(
                typeof field.metadata?.signerKey === "string" && field.metadata.signerKey.trim() !== "",
            );
            setPdfExtraTargetError(null);
            setAttributeError(null);
            setOtherInputError(null);
            setOptionErrors({});
        }
    }, [field]);

    // Default signer compound when signatures load (avoid resetting whole field on signerOptionsFlat change).
    // An initials box defaults from the INITIALS signers — an initials-only form has no signature
    // ones, so it would otherwise open with nothing selected and fail validation on save.
    // Keep the subsequent-signing signer valid.
    //
    // extraPdfPersonaCompound is shared with the persona binding, so switching Binding type to
    // subsequent signing leaves a SIGNATURE compound (`client.buy\tBuyer1`) sitting in it. That
    // matches no option in subsequentSignerOptionsFlat, so the select rendered blank — and because
    // save validates this state rather than what is displayed, defaulting it only for display would
    // have stored a different signer than the one on screen.
    useEffect(() => {
        const isSubsequent =
            extraPdfBindingKind === "subsequentSigning" || pdfExtraOutputTarget === "subsequentSigning";
        if (!isSubsequent) return;
        if (subsequentSignerOptionsFlat.length === 0) return;
        if (subsequentSignerOptionsFlat.some((o) => o.compound === extraPdfPersonaCompound)) return;
        setExtraPdfPersonaCompound(subsequentSignerOptionsFlat[0].compound);
    }, [extraPdfBindingKind, pdfExtraOutputTarget, subsequentSignerOptionsFlat, extraPdfPersonaCompound]);

    useEffect(() => {
        if (!field?.pdfEditorSyntheticKind) return;
        if (field.pdfExtraSignerSelectionCompound) return;
        const opts =
            field.pdfEditorSyntheticKind === "initialsAnnotation"
                ? initialsSignerOptionsFlat
                : signerOptionsFlat;
        if (!opts.length) return;
        setSyntheticSignerCompound((c) => c || opts[0].compound);
    }, [
        field?.id,
        field?.pdfEditorSyntheticKind,
        field?.pdfExtraSignerSelectionCompound,
        signerOptionsFlat,
        initialsSignerOptionsFlat,
    ]);

    useEffect(() => {
        let isActive = true;
        getAttributeSuggestions().then((suggestions) => {
            if (isActive) {
                setAttributeSuggestions(suggestions);
            }
        });
        return () => {
            isActive = false;
        };
    }, []);

    useEffect(() => {
        let isActive = true;
        getValidationErrorMessageSuggestions().then((list) => {
            if (isActive) setValidationErrorMessageSuggestions(list);
        });
        return () => { isActive = false; };
    }, []);

    useEffect(() => {
        if (!localField?.type) return;
        let isActive = true;
        const effectiveType =
            localField.type === "input" && localField.metadata?.isTime
                ? "isTime"
                : localField.type === "input" && localField.metadata?.format
                  ? localField.metadata.format
                  : localField.type;
        getValidationSuggestionsForType(effectiveType).then((suggestions) => {
            if (isActive) setValidationSuggestionsForType(suggestions);
        });
        return () => { isActive = false; };
    }, [localField?.type, localField?.metadata?.format, localField?.metadata?.isTime]);

    // Keep extra PDF binding UI in sync with linked entries in app state (linkedFieldId)
    useEffect(() => {
        if (!field?.id || field.pdfEditorSyntheticKind) return;
        const dataLink = (additionalFields || []).find((a) => a.linkedFieldId === field.id);
        const personaLink = (signerAttributeBindings || []).find((b) => b.linkedFieldId === field.id);
        const initialerLink = (initialerAttributeBindings || []).find((b) => b.linkedFieldId === field.id);
        const sigLink = (signatureAnnotations || []).find((s) => s.linkedFieldId === field.id);
        const initLink = (initialsAnnotations || []).find((s) => s.linkedFieldId === field.id);
        // Matched on the binding's OWN id as well as linkedFieldId. A subsequent-signing field
        // usually exists only inside the block — there is no section field to link to — so the
        // synthetic field the canvas opens carries the binding's id and no linkedFieldId. Matching
        // on linkedFieldId alone found nothing, and the Signer dropdown opened blank.
        const subseqLink = (subsequentSigningBindings || []).find(
            (b) => b.id === field.id || (b.linkedFieldId && b.linkedFieldId === field.id),
        );
        if (personaLink) {
            setExtraPdfBindingKind("personaData");
            setPersonaPdfSubtype("attributePlacements");
            setExtraPdfPersonaCompound(`${personaLink.roleKey}\t${personaLink.signerKey}`);
        } else if (initialerLink) {
            setExtraPdfBindingKind("initialsData");
            setPersonaPdfSubtype("attributePlacements");
            setExtraPdfPersonaCompound(`${initialerLink.roleKey}\t${initialerLink.signerKey}`);
        } else if (dataLink) {
            setExtraPdfBindingKind("additionalField");
            setExtraPdfPersonaCompound(signerOptionsFlat[0]?.compound ?? "");
        } else if (sigLink) {
            setExtraPdfBindingKind("personaData");
            setPersonaPdfSubtype("signatureImage");
            setExtraPdfPersonaCompound(`${sigLink.roleKey}\t${sigLink.signerKey}`);
        } else if (initLink) {
            setExtraPdfBindingKind("initialsData");
            setPersonaPdfSubtype("signatureImage");
            setExtraPdfPersonaCompound(`${initLink.roleKey}\t${initLink.signerKey}`);
        } else if (subseqLink) {
            setExtraPdfBindingKind("subsequentSigning");
            setExtraPdfPersonaCompound(`${subseqLink.roleKey}\t${subseqLink.signerKey}`);
        } else {
            setExtraPdfBindingKind("standard");
            setExtraPdfPersonaCompound(signerOptionsFlat[0]?.compound ?? "");
        }
    }, [field?.id, additionalFields, signerAttributeBindings, initialerAttributeBindings, subsequentSigningBindings, signatureAnnotations, initialsAnnotations, signerOptionsFlat]);

    const effectivePatternType =
        localField?.type === "input" && localField?.metadata?.isTime
            ? "isTime"
            : localField?.type === "input" && localField?.metadata?.format
              ? localField.metadata.format
              : localField?.type || "input";

    const mergedPatternSuggestions = useMemo(() => {
        const typePatterns = validationSuggestionsForType.patterns || [];
        const usedFromTemplates = usedPatternsFromTemplates || [];
        const formatKey =
            localField?.type === "input" && localField?.metadata?.isTime
                ? "isTime"
                : localField?.type === "input" && localField?.metadata?.format
                  ? localField.metadata.format
                  : null;
        const usedByFormat = formatKey && usedPatternsByFormat[formatKey] ? usedPatternsByFormat[formatKey] : [];
        // When a format is selected (Percentage, Currency, or Time), show only that format's patterns.
        if (formatKey) {
            return Array.from(new Set([...typePatterns, ...usedByFormat]));
        }
        return Array.from(new Set([...typePatterns, ...usedFromTemplates, ...usedByFormat]));
    }, [
        validationSuggestionsForType.patterns,
        usedPatternsFromTemplates,
        usedPatternsByFormat,
        localField?.type,
        localField?.metadata?.format,
        localField?.metadata?.isTime,
    ]);

    useEffect(() => {
        let isActive = true;
        getValidationSuggestions().then((data) => {
            if (!isActive) return;
            setUsedPatternsFromTemplates(
                Array.isArray(data.usedPatternsFromTemplates) ? data.usedPatternsFromTemplates : []
            );
            setUsedPatternsByFormat(
                data.usedPatternsByFormat && typeof data.usedPatternsByFormat === "object"
                    ? data.usedPatternsByFormat
                    : {}
            );
        });
        return () => { isActive = false; };
    }, []);

    const [localOther, setLocalOther] = useState({
        checkbox: { text: "Other", value: "other" },
        input: { attributeName: "", placeholder: "Please specify...", validation: {} }
    });

    useEffect(() => {
        if (!localField) return;
        if (mode === "annotator") {
            setAttributeError(null);
            setOtherInputError(null);
            setOptionErrors({});
            return;
        }

        const reserved = new Set();

        /** PDF extra bindings (additionalFieldsJSON / persona placements) may reuse a section field's attributeName by design. */
        const isPdfSynthetic = !!localField.pdfEditorSyntheticKind;

        // Collect existing identifiers from Fields (excluding current field)
        const uniquenessFields = [...(fields || []), ...(mainFormFieldsForUniqueness || [])];
        uniquenessFields.forEach(f => {
            if (f.id === localField.id) return; // Skip current field

            if (f.attributeName) reserved.add(f.attributeName.toLowerCase());
            // Inline inputs (all content items)
            (f.metadata?.content || []).forEach((item) => {
                if (item?.attributeName) reserved.add(item.attributeName.toLowerCase());
            });
            // Other inputs
            if (f.metadata?.other?.input?.attributeName) {
                reserved.add(f.metadata.other.input.attributeName.toLowerCase());
            }
            // Option values
            if (f.metadata?.options) {
                f.metadata.options.forEach(o => {
                    if (o.value) reserved.add(o.value.toLowerCase());
                });
            }
        });

        // Collect existing identifiers from Sections
        sections.forEach(s => {
            if (s.attributeName) reserved.add(s.attributeName.toLowerCase());
        });

        const currentAttributeName = (localField.attributeName || "").trim().toLowerCase();
        const currentOtherInputName = (localOther.input?.attributeName || "").trim().toLowerCase();
        const currentOptionValues = localOptions.map(o => (o.value || "").toLowerCase()).filter(v => v);

        // 1. Validate Main Attribute Name
        if (isPdfSynthetic) {
            const sigImageOnly =
                (pdfExtraOutputTarget === "personaData" && personaPdfSubtype === "signatureImage") ||
                (pdfExtraOutputTarget === "initialsData" && personaPdfSubtype === "signatureImage") ||
                localField.pdfEditorSyntheticKind === "signatureAnnotation" ||
                localField.pdfEditorSyntheticKind === "initialsAnnotation";
            if (!sigImageOnly && !(localField.attributeName || "").trim()) {
                setAttributeError("Attribute name is required");
            } else {
                setAttributeError(null);
            }
            setOtherInputError(null);
            setOptionErrors({});
            return;
        }

        if (currentAttributeName) {
            let error = null;
            // Extra PDF → "Additional field" writes to additionalFieldsJSON, not formData; same attributeName as a section field is allowed.
            // After cloud load, linkedFieldId may be missing (not in published JSON); still treat a matching additional row as the linked case,
            // but only when no *other* field already uses this attributeName (avoid masking real duplicates).
            const hasAdditionalRowForSameAttribute = (additionalFields || []).some(
                (a) => (a.attributeName || "").trim().toLowerCase() === currentAttributeName
            );
            const hasSubsequentRowForSameAttribute = (subsequentSigningBindings || []).some(
                (a) => (a.attributeName || "").trim().toLowerCase() === currentAttributeName
            );
            const anotherFieldUsesName = uniquenessFields.some(
                (f) =>
                    f.id !== localField.id &&
                    (f.attributeName || "").trim().toLowerCase() === currentAttributeName
            );
            const allowDuplicateVsForm =
                (extraPdfBindingKind === "additionalField" ||
                    extraPdfBindingKind === "personaData" ||
                    extraPdfBindingKind === "initialsData" ||
                    extraPdfBindingKind === "subsequentSigning" ||
                    (hasAdditionalRowForSameAttribute && !anotherFieldUsesName) ||
                    (hasSubsequentRowForSameAttribute && !anotherFieldUsesName));
            if (!allowDuplicateVsForm && reserved.has(currentAttributeName)) {
                error = "Attribute name must be unique (exists in form)";
            }
            // Check against self-components
            if (currentAttributeName === currentOtherInputName) error = "Cannot match 'Other' input name";
            if (currentOptionValues.includes(currentAttributeName)) error = "Cannot match an option value";
            
            setAttributeError(error);
        } else {
            setAttributeError(null);
        }

        // 2. Validate Other Input Attribute Name
        if (currentOtherInputName) {
            let error = null;
            if (reserved.has(currentOtherInputName)) error = "Attribute name must be unique (exists in form)";
            if (currentOtherInputName === currentAttributeName) error = "Cannot match field attribute name";
            if (currentOptionValues.includes(currentOtherInputName)) error = "Cannot match an option value";
            
            setOtherInputError(error);
        } else {
            setOtherInputError(null);
        }

        // 3. Validate Options
        const newOptionErrors = {};
        localOptions.forEach((opt, idx) => {
            const val = (opt.value || "").toLowerCase();
            if (!val) return;

            if (reserved.has(val)) {
                newOptionErrors[idx] = "Value must be unique (exists in form)";
            }
            // Check against current field attributes
            if (val === currentAttributeName) newOptionErrors[idx] = "Cannot match field attribute name";
            if (val === currentOtherInputName) newOptionErrors[idx] = "Cannot match 'Other' input name";
            
            // Check duplicates within options
            const duplicateCount = currentOptionValues.filter(v => v === val).length;
            if (duplicateCount > 1) {
                 newOptionErrors[idx] = "Duplicate option value";
            }
        });
        setOptionErrors(newOptionErrors);

    }, [
        localField?.attributeName,
        localField?.id,
        localField?.pdfEditorSyntheticKind,
        extraPdfBindingKind,
        pdfExtraOutputTarget,
        personaPdfSubtype,
        localOther.input?.attributeName,
        localOptions,
        fields,
        sections,
        additionalFields,
        subsequentSigningBindings,
        mode,
        mainFormFieldsForUniqueness,
    ]);

    /** Primary or first option/other box — drives Extra PDF output for checkbox/radio/decision fields. */
    const effectiveExtraPdfBinding = useMemo(() => {
        if (!localField) return null;
        const merged = {
            ...localField,
            metadata: {
                ...localField.metadata,
                options:
                    Array.isArray(localOptions) && localOptions.length > 0
                        ? localOptions
                        : localField.metadata?.options,
            },
        };
        return getEffectivePdfLocationForExtraBinding(merged);
    }, [localField, localOptions]);

    if (!localField) return null;

    /**
     * Persist the Days After type the dialog was showing when it was only ever implied.
     * The rule lives in utils/daysAfterType.js so the backfill script applies exactly the same one.
     * Only fills a gap — an explicit flag is never overwritten — and only on save, never on open.
     */
    const withResolvedDaysAfterType = (payload, daysAfter) => {
        const flag = missingDaysAfterTypeFlag(payload?.metadata, daysAfter);
        if (!flag) return payload;
        return { ...payload, metadata: { ...payload.metadata, [flag]: true } };
    };

    const handleSave = () => {
        if (
            mode !== "annotator" &&
            (attributeError || otherInputError || Object.keys(optionErrors).length > 0)
        ) {
            return;
        }

        setPdfExtraTargetError(null);
        if (
            localField.pdfEditorSyntheticKind &&
            (pdfExtraOutputTarget === "personaData" || pdfExtraOutputTarget === "initialsData")
        ) {
            // Initials binding is gated on INITIALS signers, not signature ones. An initials-only
            // form has no signaturesJSON roles at all, so gating on those blocked saving a field
            // on exactly the forms the binding exists for.
            const isInitials = pdfExtraOutputTarget === "initialsData";
            const options = isInitials ? initialsSignerOptionsFlat : signerOptionsFlat;
            if (options.length === 0) {
                setPdfExtraTargetError(
                    isInitials
                        ? "Tick this role under Form Settings → Initials Roles first."
                        : "Add signers under Form Settings first.",
                );
                return;
            }
            if (!syntheticSignerCompound || !String(syntheticSignerCompound).includes("\t")) {
                setPdfExtraTargetError(
                    isInitials
                        ? "Select an initials signer for initials data output."
                        : "Select a signer for persona data output.",
                );
                return;
            }
        }
        if (localField.pdfEditorSyntheticKind && pdfExtraOutputTarget === "subsequentSigning") {
            if (subsequentSignerOptionsFlat.length === 0) {
                setPdfExtraTargetError("Tick a role under Subsequent Signing Roles first.");
                return;
            }
            if (!extraPdfPersonaCompound) {
                setPdfExtraTargetError("Select the signer for subsequent signing output.");
                return;
            }
        }

        const resolvedField = { ...localField };

        let checkboxRadioOtherBlock = {};
        if (["checkboxWithOther", "radioWithOther"].includes(localField.type)) {
            const key = localField.type === "radioWithOther" ? "radio" : "checkbox";
            const existing = localOther[key] || localOther.checkbox || localOther.radio || {};
            const text = localOther.checkbox?.text || localOther.radio?.text || "Other";
            const value = localOther.checkbox?.value || localOther.radio?.value || "other";
            checkboxRadioOtherBlock = {
                other: {
                    [key]: { ...existing, text, value },
                    input: {
                        ...localOther.input,
                        attributeName: value + "Input"
                    }
                }
            };
        }

        const decisionOtherBlock = localField.type === "decisionWithOther"
            ? {
                other: {
                    decisionValue: localOther.decisionValue ?? "other",
                    input: { ...localOther.input }
                }
            }
            : {};

        let metadata = {
            ...resolvedField.metadata,
            options: localOptions
                .filter(opt => opt.value || opt.text) // Filter out empty options
                .map((option) => {
                    const cleanOption = { ...option };
                    if (localField.type === "decision" || localField.type === "decisionWithOther") {
                        cleanOption.goTo = option.goTo || null;
                    } else {
                        delete cleanOption.goTo;
                    }
                    return cleanOption;
                }),
            ...checkboxRadioOtherBlock,
            ...decisionOtherBlock
        };

        // Ensure datetime fields have format in metadata.fieldData
        if (resolvedField.type === "datetime") {
            if (!metadata.fieldData) {
                metadata.fieldData = {};
            }
            // Ensure format is always present
            if (!metadata.fieldData.format) {
                metadata.fieldData.format = "01/02/2006";
            }
            // Preserve mec if it exists, otherwise don't add it (optional)
            // mec will be handled by the UI checkbox state
        }

        if (resolvedField.formUpload) {
            metadata.formUploadData = resolvedField.attributeName;
        } else if (metadata.formUploadData) {
            delete metadata.formUploadData;
        }
        delete metadata.offerCompareData;
        delete metadata.pdfLocationContinuations;

        const updatedField = {
            ...resolvedField,
            /** Do not normalize casing; preserve author-entered attributeName (trim edges only). */
            attributeName:
                resolvedField.attributeName === undefined || resolvedField.attributeName === null
                    ? resolvedField.attributeName
                    : String(resolvedField.attributeName).trim(),
            // Remove field-level goTo for decision fields
            ...((resolvedField.type === "decision" || resolvedField.type === "decisionWithOther") &&
                resolvedField.goTo && { goTo: undefined }),
            metadata,
        };

        if (
            onFieldPdfBindingSync &&
            updatedField.id &&
            !resolvedField.pdfEditorSyntheticKind
        ) {
            // Use same placement source as the Extra PDF preview: `updatedField` strips options without
            // value/text, which can drop option-level rects and falsely clear persona/additional bindings.
            const effPdf =
                effectiveExtraPdfBinding ?? getEffectivePdfLocationForExtraBinding(updatedField);
            const hasPdf = !!effPdf?.pdfLocation?.page;
            const bindingKey = hasPdf ? extraPdfBindingKind : "standard";
            let syncKind =
                bindingKey === "standard"
                    ? "none"
                    : bindingKey === "additionalField"
                      ? "data"
                      : bindingKey === "subsequentSigning"
                        ? "subsequentSigning"
                        : bindingKey === "personaData"
                          ? personaPdfSubtype === "signatureImage"
                            ? "signatureImage"
                            : "persona"
                          : bindingKey === "initialsData"
                            ? personaPdfSubtype === "signatureImage"
                              ? "initialsImage"
                              : "initialsPersona"
                          : "none";
            // Initials kinds are gated on the initials signer list; signature kinds on the
            // signature one. Gating both on signatures silently dropped initials sync to "none"
            // on initials-only forms.
            const syncOptions =
                syncKind === "initialsPersona" || syncKind === "initialsImage"
                    ? initialsSignerOptionsFlat
                    : signerOptionsFlat;
            if (
                (syncKind === "persona" ||
                    syncKind === "signatureImage" ||
                    syncKind === "initialsPersona" ||
                    syncKind === "initialsImage") &&
                syncOptions.length === 0
            ) {
                syncKind = "none";
            }
            if (
                syncKind === "subsequentSigning" &&
                (!extraPdfPersonaCompound || subsequentSignerOptionsFlat.length === 0)
            ) {
                syncKind = "none";
            }
            onFieldPdfBindingSync({
                fieldId: updatedField.id,
                kind: syncKind,
                attributeName: updatedField.attributeName,
                pdfLocation: effPdf?.pdfLocation || null,
                lines:
                    effPdf?.lines != null && Number(effPdf.lines) > 0
                        ? Number(effPdf.lines)
                        : updatedField.annotationMultiline ??
                          effPdf?.pdfLocation?.lines ??
                          0,
                personaCompound:
                    syncKind === "persona" ||
                    syncKind === "signatureImage" ||
                    syncKind === "initialsPersona" ||
                    syncKind === "initialsImage" ||
                    syncKind === "subsequentSigning"
                        ? extraPdfPersonaCompound
                        : null,
            });
        }

        let savePayload =
            resolvedField.pdfEditorSyntheticKind
                ? {
                      ...updatedField,
                      _pdfExtraOutputTarget:
                          pdfExtraOutputTarget === "subsequentSigning"
                              ? "subsequentSigningJSON"
                              : pdfExtraOutputTarget === "initialsData" && personaPdfSubtype === "signatureImage"
                                ? "initialsJSON_signatureImage"
                                : pdfExtraOutputTarget === "initialsData"
                                  ? "initialsJSON"
                              : pdfExtraOutputTarget,
                      _pdfPersonaSubtype:
                          pdfExtraOutputTarget === "personaData" || pdfExtraOutputTarget === "initialsData"
                              ? personaPdfSubtype
                              : undefined,
                      _pdfSyntheticSignerCompound: syntheticSignerCompound,

                  }
                : updatedField;
        // Days After type: the dropdown's selection can be DERIVED rather than stored. When a form
        // declares only relative types (a DBC-only form, say) there is no base MEC/BAD slot, so the
        // control falls back to the first option and displays "DBC" while no mecIs* flag exists on
        // the field. The label looked right and the flag was never written. Persist it on save, so
        // what the analyst saw is what gets stored — and only once they save, never on open.
        savePayload = withResolvedDaysAfterType(savePayload, daysAfterSettings);

        onSave(savePayload);
    };

    const saveDisabled =
        mode !== "annotator" &&
        (!!attributeError || !!otherInputError || Object.keys(optionErrors).length > 0);

    // ... (helper functions handleAddOption etc remain same)

    const handleAddOption = () => {
        setLocalOptions([...localOptions, { value: "", text: "", goTo: null }]);
    };

    const handleRemoveOption = (index) => {
        setLocalOptions(localOptions.filter((_, i) => i !== index));
    };

    const handleOptionChange = (index, property, value) => {
        const updatedOptions = [...localOptions];
        const nextOption = {
            ...updatedOptions[index],
            [property]: value,
        };
        if (mode === "annotator" && property === "text") {
            const nextValue = toCamelCase(value || "");
            if (!nextOption.value || nextOption.value === toCamelCase(updatedOptions[index]?.text || "")) {
                nextOption.value = nextValue;
            }
        }
        updatedOptions[index] = nextOption;
        setLocalOptions(updatedOptions);
    };

    // Helper function to get available sections for goTo
    const getAvailableSections = (currentSectionId = null) => {
        return sections
            .filter((section) => section.id !== currentSectionId) // Exclude current section
            .map((section) => ({
                value: section.attributeName,
                label: section.title,
            }));
    };

    const isOptionType = [
        "checkbox",
        "checkboxWithOther",
        "radio",
        "radioWithOther",
        "decision",
        "decisionWithOther",
    ].includes(localField.type);

    /** PDF Add Signature / Add Initials image box: only signer + save/delete apply; no form-field JSON. */
    const isAnnotationImageBoxEditor =
        localField.pdfEditorSyntheticKind === "signatureAnnotation" ||
        localField.pdfEditorSyntheticKind === "initialsAnnotation";
    // An initials box binds an INITIALS signer. Naming both "Signer" made an initials tab read
    // as a signature tab in the editor.
    const isInitialsBoxEditor = localField.pdfEditorSyntheticKind === "initialsAnnotation";
    const signerFieldLabel = isInitialsBoxEditor ? "Initials Signer" : "Signer";
    // An initials box picks from the initials signers; a signature box from signaturesJSON.
    const boxSignerOptions = isInitialsBoxEditor ? initialsSignerOptionsFlat : signerOptionsFlat;
    // The "Extra PDF output" persona/initials select serves both bindings from one block.
    const extraPdfSignerOptions =
        extraPdfBindingKind === "initialsData" ? initialsSignerOptionsFlat : signerOptionsFlat;

    const handleTypeChange = (newType) => {
        const optionTypes = ["checkbox", "checkboxWithOther", "radio", "radioWithOther", "decision", "decisionWithOther"];
        const next = { ...localField, type: newType };
        if (!next.metadata) next.metadata = {};
        if (optionTypes.includes(newType)) {
            const options = localOptions?.length ? localOptions : [{ value: "", text: "", goTo: (newType === "decision" || newType === "decisionWithOther") ? null : undefined }];
            next.metadata = { ...next.metadata, options };
            if (newType === "checkboxWithOther" || newType === "radioWithOther") {
                next.metadata.other = next.metadata.other || {
                    [newType === "radioWithOther" ? "radio" : "checkbox"]: { text: "Other", value: "other" },
                    input: { attributeName: (next.metadata.other?.input?.attributeName) || "otherInput", placeholder: "Please specify...", validation: {} },
                };
            }
            if (newType === "decisionWithOther") {
                next.metadata.other = next.metadata.other || {
                    decisionValue: next.metadata.other?.decisionValue ?? "other",
                    input: { attributeName: (next.metadata.other?.input?.attributeName) || "otherInput", placeholder: "Please specify...", validation: {} },
                };
            }
        }
        if (newType === "datetime") {
            next.metadata.fieldData = next.metadata.fieldData || { format: "01/02/2006" };
        }
        if (newType === "inlineInput") {
            const attr = localField.attributeName || "value";
            next.metadata.content = next.metadata.content?.length ? next.metadata.content : [
                { attributeName: attr, placeholder: "", postLabel: "", preLabel: "", validation: {}, validationErrorMessage: null, value: null, width: "100px" },
            ];
        }
        if (newType === "decision" || newType === "decisionWithOther") {
            next.goTo = next.goTo ?? null;
        }
        setLocalField(next);
        if (optionTypes.includes(newType)) setLocalOptions(next.metadata.options || []);
    };

    const dialogBody = (
        <>
            <DialogTitle
                sx={
                    variant === "panel"
                        ? { display: "flex", alignItems: "center", justifyContent: "space-between", pr: 2 }
                        : undefined
                }
            >
                {isAnnotationImageBoxEditor ? signerFieldLabel : "Edit Field"}
                {variant === "panel" && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            color="primary"
                            size="small"
                            disabled={saveDisabled}
                        >
                            Save
                        </Button>
                        <IconButton onClick={onClose} size="small" aria-label="Close">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                )}
            </DialogTitle>
            <DialogContent
                sx={
                    variant === "panel"
                        ? {
                              flex: 1,
                              overflowY: "auto",
                              // Extra top room so outlined InputLabels aren’t clipped under Paper overflow:hidden
                              pt: 3.5,
                              scrollPaddingTop: 8,
                          }
                        : { pt: 3 }
                }
            >
                {isAnnotationImageBoxEditor && (
                    <Box sx={{ mb: 2, mt: 0.5 }}>
                        {/*
                          No MUI InputLabel: floating/notched labels still clip under parent Paper overflow:hidden.
                          Title already reads "Signer"/"Initials Signer"; Select uses aria-label for assistive tech.
                        */}
                        <FormControl
                            fullWidth
                            size="small"
                            variant="outlined"
                            error={!!pdfExtraTargetError}
                            disabled={boxSignerOptions.length === 0}
                        >
                            <Select
                                id="annotation-image-signer-select"
                                aria-label={signerFieldLabel}
                                value={
                                    boxSignerOptions.length === 0
                                        ? ""
                                        : boxSignerOptions.some((o) => o.compound === syntheticSignerCompound)
                                          ? syntheticSignerCompound
                                          : boxSignerOptions[0]?.compound || ""
                                }
                                onChange={(e) => {
                                    setSyntheticSignerCompound(e.target.value);
                                    setPdfExtraTargetError(null);
                                }}
                            >
                                {boxSignerOptions.map((o) => (
                                    <MenuItem key={o.compound} value={o.compound}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            {(pdfExtraTargetError || boxSignerOptions.length === 0) && (
                                <FormHelperText>
                                    {pdfExtraTargetError ||
                                        (isInitialsBoxEditor
                                            ? "Tick this role under Form Settings → Initials Roles first."
                                            : "Add roles and signers under Form Settings first.")}
                                </FormHelperText>
                            )}
                        </FormControl>
                    </Box>
                )}
                {mode !== "annotator" &&
                    localField.pdfEditorSyntheticKind &&
                    !isAnnotationImageBoxEditor && (
                        <Box sx={{ mb: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Extra PDF output
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                Also publish this field&apos;s PDF box as a generic binding (same coordinates). Standard
                                keeps the section field only; additional field and persona data are not in formData.
                                Signature and initials image boxes use <strong>Add Signature</strong> /{" "}
                                <strong>Add Initials</strong> on the PDF, not this dialog.
                            </Typography>
                            <FormControl component="fieldset" variant="standard" sx={{ width: "100%" }}>
                                <FormLabel component="legend" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                                    Binding type
                                </FormLabel>
                                <RadioGroup
                                    value={pdfExtraOutputTarget}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setPdfExtraOutputTarget(v);
                                        setPdfExtraTargetError(null);
                                        if (v === "personaData") setPersonaPdfSubtype("attributePlacements");
                                        if (v === "initialsData") setPersonaPdfSubtype("signatureImage");
                                    }}
                                >
                                    <FormControlLabel
                                        value="standard"
                                        control={<Radio size="small" />}
                                        label="Standard (section field only)"
                                    />
                                    <FormControlLabel
                                        value="additionalField"
                                        control={<Radio size="small" />}
                                        label="Additional field (not in formData)"
                                    />
                                    <FormControlLabel
                                        value="personaData"
                                        control={<Radio size="small" />}
                                        label="Signer field (not in formData, related to signers)"
                                    />
                                    <FormControlLabel
                                        value="initialsData"
                                        control={<Radio size="small" />}
                                        label="Initials data (not in formData, related to signers)"
                                    />
                                    <FormControlLabel
                                        value="subsequentSigning"
                                        control={<Radio size="small" />}
                                        label="Subsequent signing (not in formData)"
                                    />
                                </RadioGroup>
                            </FormControl>
                            {pdfExtraTargetError && (
                                <Alert severity="error" sx={{ mt: 1 }}>
                                    {pdfExtraTargetError}
                                </Alert>
                            )}
                            {pdfExtraOutputTarget === "personaData" && (
                                <Box sx={{ mt: 1.5 }}>
                                    {signerOptionsFlat.length > 0 ? (
                                        <FormControl fullWidth size="small" variant="outlined">
                                            <InputLabel id="synthetic-signer-extra-label" shrink>
                                                Signer
                                            </InputLabel>
                                            <Select
                                                labelId="synthetic-signer-extra-label"
                                                label="Signer"
                                                value={
                                                    signerOptionsFlat.some((o) => o.compound === syntheticSignerCompound)
                                                        ? syntheticSignerCompound
                                                        : signerOptionsFlat[0]?.compound || ""
                                                }
                                                onChange={(e) => {
                                                    setSyntheticSignerCompound(e.target.value);
                                                    setPdfExtraTargetError(null);
                                                }}
                                            >
                                                {signerOptionsFlat.map((o) => (
                                                    <MenuItem key={o.compound} value={o.compound}>
                                                        {o.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <Alert severity="warning" sx={{ mt: 1 }}>
                                            Add roles and signers under Form Settings to use persona binding.
                                        </Alert>
                                    )}
                                </Box>
                            )}
                            {pdfExtraOutputTarget === "initialsData" && (
                                <Box sx={{ mt: 1.5 }}>
                                    {initialsSignerOptionsFlat.length > 0 ? (
                                        <FormControl fullWidth size="small" variant="outlined">
                                            <InputLabel id="synthetic-initials-signer-extra-label" shrink>
                                                Initials Signer
                                            </InputLabel>
                                            <Select
                                                labelId="synthetic-initials-signer-extra-label"
                                                label="Initials Signer"
                                                value={
                                                    initialsSignerOptionsFlat.some((o) => o.compound === syntheticSignerCompound)
                                                        ? syntheticSignerCompound
                                                        : initialsSignerOptionsFlat[0]?.compound || ""
                                                }
                                                onChange={(e) => {
                                                    setSyntheticSignerCompound(e.target.value);
                                                    setPdfExtraTargetError(null);
                                                }}
                                            >
                                                {initialsSignerOptionsFlat.map((o) => (
                                                    <MenuItem key={o.compound} value={o.compound}>
                                                        {o.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <Alert severity="warning" sx={{ mt: 1 }}>
                                            Tick this role under Form Settings → Initials Roles to use initials binding.
                                        </Alert>
                                    )}
                                </Box>
                            )}
                            {pdfExtraOutputTarget === "initialsData" && (
                                <FormControl component="fieldset" variant="standard" sx={{ mt: 2, width: "100%" }}>
                                    <FormLabel component="legend" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                                        Initials output type
                                    </FormLabel>
                                    <RadioGroup
                                        value={personaPdfSubtype}
                                        onChange={(e) => setPersonaPdfSubtype(e.target.value)}
                                    >
                                        <FormControlLabel
                                            value="signatureImage"
                                            control={<Radio size="small" />}
                                            label="Initials image (pdfLocation)"
                                        />
                                        <FormControlLabel
                                            value="attributePlacements"
                                            control={<Radio size="small" />}
                                            label="Initials persona (attribute placements)"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            )}
                            {pdfExtraOutputTarget === "subsequentSigning" && (
                                <Box sx={{ mt: 1.5 }}>
                                    {subsequentSignerOptionsFlat.length > 0 ? (
<FormControl fullWidth size="small" variant="outlined">
                                            <InputLabel id="synthetic-subseq-signer-label" shrink>
                                                Signer
                                            </InputLabel>
                                            <Select
                                                labelId="synthetic-subseq-signer-label"
                                                label="Signer"
                                                value={extraPdfPersonaCompound || ""}
                                                onChange={(e) => {
                                                    setExtraPdfPersonaCompound(e.target.value);
                                                    setPdfExtraTargetError(null);
                                                }}
                                            >
                                                {subsequentSignerOptionsFlat.map((o) => (
                                                    <MenuItem key={o.compound} value={o.compound}>
                                                        {o.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <Alert severity="warning" sx={{ mt: 1 }}>
                                            Tick a role under Subsequent Signing Roles to use subsequent signing binding.
                                        </Alert>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}
                {/* Field type selector (JSON only) - always visible so user can change type for any field */}
                {mode !== "annotator" && !localField.pdfEditorSyntheticKind && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "wrap",
                            gap: 2,
                            alignItems: "flex-start",
                            mt: 2,
                            mb: 2,
                            width: "100%",
                            minWidth: 0,
                            // Do not set overflowX: "auto" here — it creates a scroll container that clips
                            // MUI outlined InputLabels (position:absolute above the field).
                        }}
                    >
                        <FormControl size="small" sx={{ minWidth: 200, flexShrink: 0 }} variant="outlined">
                            <InputLabel id="edit-field-type-label" shrink>
                                Field Type
                            </InputLabel>
                            <Select
                                labelId="edit-field-type-label"
                                label="Field Type"
                                value={localField.type || "input"}
                                onChange={(e) => handleTypeChange(e.target.value)}
                            >
                                {fieldTypeSelectOptions.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t === "checkboxWithOther" && "Checkbox with Other"}
                                        {t === "radioWithOther" && "Radio with Other"}
                                        {t === "inlineInput" && "Inline Input"}
                                        {t === "decisionWithOther" && "Decision with Other"}
                                        {t !== "checkboxWithOther" && t !== "radioWithOther" && t !== "inlineInput" && t !== "decisionWithOther" && t.charAt(0).toUpperCase() + t.slice(1)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {showAttachPlacedRow && (
                            <FormControl
                                size="small"
                                sx={{ minWidth: 240, flex: "1 1 240px", maxWidth: 400 }}
                                variant="outlined"
                                disabled={attachCandidates.length === 0}
                            >
                                <InputLabel id="attach-placed-box-label" shrink>
                                    Attach box to field
                                </InputLabel>
                                <Select
                                    labelId="attach-placed-box-label"
                                    label="Attach box to field"
                                    value={attachCandidates.length === 0 ? "" : attachToFieldId}
                                    displayEmpty
                                    renderValue={(selected) => {
                                        if (attachCandidates.length === 0) {
                                            return (
                                                <Typography component="span" variant="body2" color="text.secondary">
                                                    No targets available
                                                </Typography>
                                            );
                                        }
                                        if (selected === "" || selected == null) {
                                            return (
                                                <Typography component="span" variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                                    Select field…
                                                </Typography>
                                            );
                                        }
                                        const f = attachCandidates.find((x) => x.id === selected);
                                        if (!f) return "";
                                        return `${f.label || f.attributeName} (${f.attributeName})`;
                                    }}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (!v) {
                                            setAttachToFieldId("");
                                            return;
                                        }
                                        if (
                                            !window.confirm(
                                                "Move this PDF box to the selected field and remove the current field row? You can undo via Form JSON if needed.",
                                            )
                                        ) {
                                            setAttachToFieldId("");
                                            return;
                                        }
                                        onAttachPlacedAnnotation(localField.id, v);
                                        setAttachToFieldId("");
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>{attachCandidates.length === 0 ? "No targets available" : "Select field…"}</em>
                                    </MenuItem>
                                    {attachCandidates.map((f) => (
                                        <MenuItem key={f.id} value={f.id}>
                                            {(f.label || f.attributeName)} ({f.attributeName})
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    {attachCandidates.length === 0
                                        ? "No other fields of this type without a PDF box yet. Import or add fields first."
                                        : "Move this drawn box onto an existing form field row."}
                                </FormHelperText>
                            </FormControl>
                        )}
                    </Box>
                )}
                {/* Basic properties for non-inlineInput fields (hidden for signature image — only Signer affects signaturesJSON) */}
                {localField.type !== "inlineInput" && !isAnnotationImageBoxEditor && (
                    <Box sx={{ mb: 4 }}>
                        <Typography
                            variant="subtitle1"
                            color="primary"
                            gutterBottom
                            sx={{ fontWeight: 500 }}
                        >
                            Basic Properties
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                            {mode !== "annotator" && (
                                <TextField
                                    label="Label"
                                    value={localField.label || ""}
                                    onChange={(e) =>
                                        setLocalField({ ...localField, label: e.target.value })
                                    }
                                    sx={{ flexGrow: 1 }}
                                />
                            )}
                            <TextField
                                label="Field Notes"
                                placeholder="Seller Only, Read Only, etc"
                                value={localField.fieldNotes || ""}
                                onChange={(e) =>
                                    setLocalField({ ...localField, fieldNotes: e.target.value })
                                }
                                sx={{ flexGrow: 1 }}
                            />
                            {mode !== "annotator" && localField.type !== "decision" && localField.type !== "decisionWithOther" && (
                                <TextField
                                    label="Placeholder"
                                    value={localField.placeholder || ""}
                                    onChange={(e) =>
                                        setLocalField({
                                            ...localField,
                                            placeholder: e.target.value,
                                        })
                                    }
                                    sx={{ flexGrow: 1 }}
                                />
                            )}
                        </Box>
                        {mode !== "annotator" && (
                            <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                                <Autocomplete
                                    freeSolo
                                    options={attributeSuggestions}
                                    value={localField.attributeName || ""}
                                    onChange={(_, value) =>
                                        setLocalField({
                                            ...localField,
                                            attributeName: value || "",
                                        })
                                    }
                                    onInputChange={(_, value) =>
                                        setLocalField({
                                            ...localField,
                                            attributeName: value,
                                        })
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            label="Attribute Name"
                                            error={!!attributeError}
                                            helperText={attributeError || ""}
                                        />
                                    )}
                                    sx={{ width: "100%" }}
                                />
                                {localField.type !== "decision" && localField.type !== "decisionWithOther" && (
                                    <FormControl fullWidth>
                                        <InputLabel>Section</InputLabel>
                                        <Select
                                            label="Section"
                                            value={localField.sectionId || ""}
                                            onChange={(e) => {
                                                // A field assigned here may have no order yet
                                                // (CreateFieldDialog / instant-create leave it unset),
                                                // which would publish without an `order` key. Drop it
                                                // at the bottom of the target section — same rule the
                                                // other assign paths use.
                                                const sid = e.target.value;
                                                const maxOrder = (fields || [])
                                                    .filter((f) => f.sectionId === sid && !f.isOverflow)
                                                    .reduce((m, f) => Math.max(m, f.order || 0), 0);
                                                setLocalField({
                                                    ...localField,
                                                    sectionId: sid,
                                                    order: localField.order ?? maxOrder + 1,
                                                });
                                            }}
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            {sections.map((section) => (
                                                <MenuItem key={section.id} value={section.id}>
                                                    {section.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Box>
                        )}
                        {mode !== "annotator" && (
                            <>
                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
                                    Annotation
                                </Typography>
                                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
                                    <TextField
                                        label="Annotation multi-line (lines)"
                                        type="number"
                                        inputProps={{ min: 0, step: 1 }}
                                        value={localField.annotationMultiline ?? 0}
                                        onChange={(e) => {
                                            const v = e.target.value === "" ? 0 : parseInt(e.target.value, 10);
                                            setLocalField({
                                                ...localField,
                                                annotationMultiline: isNaN(v) ? 0 : Math.max(0, v),
                                            });
                                        }}
                                        helperText="0 = none; e.g. 3 for overflow box, 11 for wrap-in-box"
                                        sx={{ minWidth: 140 }}
                                        size="small"
                                    />
                                    <FormControl size="small" sx={{ minWidth: 220 }}>
                                        <InputLabel id="edit-field-is-overflow-label">Is Overflow</InputLabel>
                                        <Select
                                            labelId="edit-field-is-overflow-label"
                                            label="Is Overflow"
                                            value={localField.isOverflow ?? ""}
                                            onChange={(e) =>
                                                setLocalField({
                                                    ...localField,
                                                    isOverflow: e.target.value || null,
                                                })
                                            }
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            {fields
                                                .filter((f) => !f.isOverflow && f.attributeName && f.id !== localField?.id)
                                                .map((f) => (
                                                    <MenuItem key={f.id} value={f.attributeName}>
                                                        {f.attributeName}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                        {localField.isOverflow && (
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                                                This box is location-only; it will be stored under the target field&apos;s metadata.overflow.
                                            </Typography>
                                        )}
                                    </FormControl>
                                    {mode === "json" && (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={localField.excludeFromFormJSON === true}
                                                    onChange={(e) =>
                                                        setLocalField({
                                                            ...localField,
                                                            excludeFromFormJSON: e.target.checked,
                                                        })
                                                    }
                                                />
                                            }
                                            label="Exclude from form JSON"
                                            sx={{ mt: 1, display: "block" }}
                                        />
                                    )}
                                </Box>
                            </>
                        )}
                        {mode !== "annotator" && localField.type !== "decision" && localField.type !== "decisionWithOther" && (
                            <Box sx={{ mt: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={localField.validation?.required === true}
                                            onChange={(e) => {
                                                const isRequired = e.target.checked;
                                                setLocalField({
                                                    ...localField,
                                                    validation: {
                                                        ...localField.validation,
                                                        required: isRequired ? true : null,
                                                    },
                                                    validationErrorMessage: isRequired
                                                        ? "Required"
                                                        : null,
                                                    rules: {
                                                        ...(localField.rules || {}),
                                                        notes: isRequired
                                                            ? localField.rules?.notes || ""
                                                            : "",
                                                    },
                                                });
                                            }}
                                            color="primary"
                                        />
                                    }
                                    label="Required Field"
                                />
                                {localField.validation?.required === true && (
                                    <TextField
                                        label="Notes"
                                        placeholder="Why is this field Required?"
                                        value={localField.rules?.notes || ""}
                                        onChange={(e) =>
                                            setLocalField({
                                                ...localField,
                                                rules: {
                                                    ...(localField.rules || {}),
                                                    notes: e.target.value,
                                                },
                                            })
                                        }
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        sx={{ mt: 1 }}
                                    />
                                )}
                            </Box>
                        )}
                        {/* Help text & default value */}
                        {mode !== "annotator" && localField.type !== "decision" && localField.type !== "inlineInput" && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Help text & default value
                                </Typography>
                                <Stack spacing={2}>
                                    <TextField
                                        label="Info (help text)"
                                        placeholder="Shown to user filling the form"
                                        value={localField.info ?? ""}
                                        onChange={(e) =>
                                            setLocalField({
                                                ...localField,
                                                info: e.target.value || null,
                                            })
                                        }
                                        fullWidth
                                        multiline
                                        minRows={1}
                                        size="small"
                                    />
                                    <TextField
                                        label="Hint"
                                        placeholder="Hint for the field"
                                        value={localField.hint ?? ""}
                                        onChange={(e) =>
                                            setLocalField({
                                                ...localField,
                                                hint: e.target.value || null,
                                            })
                                        }
                                        fullWidth
                                        size="small"
                                    />
                                    <TextField
                                        label="Default value"
                                        placeholder="Pre-filled value"
                                        value={localField.value ?? ""}
                                        onChange={(e) =>
                                            setLocalField({
                                                ...localField,
                                                value: e.target.value || null,
                                            })
                                        }
                                        fullWidth
                                        size="small"
                                    />
                                </Stack>
                            </Box>
                        )}
                        {/* Constraints */}
                        {mode !== "annotator" && localField.type !== "decision" && localField.type !== "inlineInput" && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Constraints
                                </Typography>
                                <Stack spacing={2}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Constraint</InputLabel>
                                        <Select
                                            label="Constraint"
                                            value={localField.constraint ?? ""}
                                            onChange={(e) =>
                                                setLocalField({
                                                    ...localField,
                                                    constraint: e.target.value || null,
                                                })
                                            }
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            <MenuItem value="required">Required</MenuItem>
                                            <MenuItem value="conditional">Conditional</MenuItem>
                                        </Select>
                                    </FormControl>
                                    {localField.constraint === "conditional" && (
                                        <TextField
                                            label="Constraint ID"
                                            placeholder="ID linking to constraint logic"
                                            value={localField.constraintId ?? ""}
                                            onChange={(e) =>
                                                setLocalField({
                                                    ...localField,
                                                    constraintId: e.target.value || null,
                                                })
                                            }
                                            fullWidth
                                            size="small"
                                        />
                                    )}
                                    <TextField
                                        label="Event trigger"
                                        placeholder="Event trigger configuration"
                                        value={localField.eventTrigger ?? ""}
                                        onChange={(e) =>
                                            setLocalField({
                                                ...localField,
                                                eventTrigger: e.target.value || null,
                                            })
                                        }
                                        fullWidth
                                        size="small"
                                    />
                                </Stack>
                            </Box>
                        )}
                        {/* Validation (min/max, length, patterns, error message) */}
                        {mode !== "annotator" && localField.type !== "decision" && localField.type !== "inlineInput" && (
                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1 }}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Validation
                                    </Typography>
                                    {localField.type === "input" && (
                                        <Stack direction="row" spacing={2}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={localField.metadata?.format === "percentage"}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                getValidationSuggestionsForType("percentage").then((suggestions) => {
                                                                    setLocalField((prev) => ({
                                                                        ...prev,
                                                                        metadata: {
                                                                            ...prev.metadata,
                                                                            format: "percentage",
                                                                            isTime: undefined,
                                                                        },
                                                                        validation: {
                                                                            ...prev.validation,
                                                                            ...suggestions.validation,
                                                                            patterns: suggestions.patterns?.length ? suggestions.patterns : prev.validation?.patterns ?? null,
                                                                        },
                                                                        validationErrorMessage: suggestions.suggestedErrorMessage ?? prev.validationErrorMessage,
                                                                        placeholder: suggestions.suggestedPlaceholder ?? prev.placeholder,
                                                                    }));
                                                                });
                                                            } else {
                                                                setLocalField({
                                                                    ...localField,
                                                                    metadata: {
                                                                        ...localField.metadata,
                                                                        format: undefined,
                                                                        // Sibling of `format` — meaningless without one. Clear
                                                                        // so it doesn't sit orphaned in the output JSON.
                                                                        stripFormatterSymbol: undefined,
                                                                        isTime: localField.metadata?.isTime,
                                                                    },
                                                                });
                                                            }
                                                        }}
                                                    />
                                                }
                                                label="Percentage"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={localField.metadata?.format === "currency"}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                getValidationSuggestionsForType("currency").then((suggestions) => {
                                                                    setLocalField((prev) => ({
                                                                        ...prev,
                                                                        metadata: {
                                                                            ...prev.metadata,
                                                                            format: "currency",
                                                                            isTime: undefined,
                                                                        },
                                                                        validation: {
                                                                            ...prev.validation,
                                                                            ...suggestions.validation,
                                                                            patterns: suggestions.patterns?.length ? suggestions.patterns : prev.validation?.patterns ?? null,
                                                                        },
                                                                        validationErrorMessage: suggestions.suggestedErrorMessage ?? prev.validationErrorMessage,
                                                                        placeholder: suggestions.suggestedPlaceholder ?? prev.placeholder,
                                                                    }));
                                                                });
                                                            } else {
                                                                setLocalField({
                                                                    ...localField,
                                                                    metadata: {
                                                                        ...localField.metadata,
                                                                        format: undefined,
                                                                        // Sibling of `format` — meaningless without one. Clear
                                                                        // so it doesn't sit orphaned in the output JSON.
                                                                        stripFormatterSymbol: undefined,
                                                                        isTime: localField.metadata?.isTime,
                                                                    },
                                                                });
                                                            }
                                                        }}
                                                    />
                                                }
                                                label="Currency"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={Boolean(localField.metadata?.isTime)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                getValidationSuggestionsForType("isTime").then((suggestions) => {
                                                                    setLocalField((prev) => ({
                                                                        ...prev,
                                                                        metadata: {
                                                                            ...prev.metadata,
                                                                            isTime: true,
                                                                            format: undefined,
                                                                            stripFormatterSymbol: undefined,
                                                                        },
                                                                        validation: {
                                                                            ...prev.validation,
                                                                            ...suggestions.validation,
                                                                            patterns: suggestions.patterns?.length ? suggestions.patterns : prev.validation?.patterns ?? null,
                                                                        },
                                                                        validationErrorMessage: suggestions.suggestedErrorMessage ?? prev.validationErrorMessage,
                                                                        placeholder: suggestions.suggestedPlaceholder ?? prev.placeholder,
                                                                    }));
                                                                });
                                                            } else {
                                                                setLocalField({
                                                                    ...localField,
                                                                    metadata: {
                                                                        ...localField.metadata,
                                                                        isTime: undefined,
                                                                        format: localField.metadata?.format,
                                                                    },
                                                                });
                                                            }
                                                        }}
                                                    />
                                                }
                                                label="Time"
                                            />
                                            {/* Strip formatter symbol — only meaningful when a
                                                currency / percentage format is set. Disabled
                                                otherwise so the flag doesn't get orphaned in the
                                                output JSON. Sibling of `metadata.format`. */}
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        disabled={
                                                            localField.metadata?.format !== "currency" &&
                                                            localField.metadata?.format !== "percentage"
                                                        }
                                                        checked={Boolean(localField.metadata?.stripFormatterSymbol)}
                                                        onChange={(e) => {
                                                            const on = e.target.checked;
                                                            setLocalField((prev) => ({
                                                                ...prev,
                                                                metadata: {
                                                                    ...prev.metadata,
                                                                    stripFormatterSymbol: on ? true : undefined,
                                                                },
                                                            }));
                                                        }}
                                                    />
                                                }
                                                label="Strip formatter symbol"
                                            />
                                        </Stack>
                                    )}
                                </Box>
                                <Stack spacing={2} direction={{ xs: "column", sm: "row" }} flexWrap="wrap" useFlexGap>
                                    <TextField
                                        label="Min"
                                        type="number"
                                        value={localField.validation?.min ?? ""}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            setLocalField({
                                                ...localField,
                                                validation: {
                                                    ...localField.validation,
                                                    min: v === "" ? null : Number(v),
                                                },
                                            });
                                        }}
                                        size="small"
                                        sx={{ width: 120 }}
                                        inputProps={{ min: undefined }}
                                    />
                                    <TextField
                                        label="Max"
                                        type="number"
                                        value={localField.validation?.max ?? ""}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            setLocalField({
                                                ...localField,
                                                validation: {
                                                    ...localField.validation,
                                                    max: v === "" ? null : Number(v),
                                                },
                                            });
                                        }}
                                        size="small"
                                        sx={{ width: 120 }}
                                    />
                                    <TextField
                                        label="Min length"
                                        type="number"
                                        value={localField.validation?.minLength ?? ""}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            setLocalField({
                                                ...localField,
                                                validation: {
                                                    ...localField.validation,
                                                    minLength: v === "" ? null : Number(v),
                                                },
                                            });
                                        }}
                                        size="small"
                                        sx={{ width: 120 }}
                                        inputProps={{ min: 0 }}
                                    />
                                    <TextField
                                        label="Max length"
                                        type="number"
                                        value={localField.validation?.maxLength ?? ""}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            setLocalField({
                                                ...localField,
                                                validation: {
                                                    ...localField.validation,
                                                    maxLength: v === "" ? null : Number(v),
                                                },
                                            });
                                        }}
                                        size="small"
                                        sx={{ width: 120 }}
                                        inputProps={{ min: 0 }}
                                    />
                                </Stack>
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                        Validation patterns (regex, one per line)
                                    </Typography>
                                    <ClickAwayListener onClickAway={() => setPatternSuggestionsAnchorEl(null)}>
                                        <Box sx={{ position: "relative" }}>
                                            <TextField
                                                placeholder="One regex per line"
                                                value={Array.isArray(localField.validation?.patterns)
                                                    ? (localField.validation.patterns || []).join("\n")
                                                    : ""}
                                                onChange={(e) => {
                                                    const raw = e.target.value || "";
                                                    const patterns = raw
                                                        .split(/\n/)
                                                        .map((s) => s.trim())
                                                        .filter(Boolean);
                                                    setLocalField({
                                                        ...localField,
                                                        validation: {
                                                            ...localField.validation,
                                                            patterns: patterns.length ? patterns : null,
                                                        },
                                                    });
                                                }}
                                                onFocus={(e) => setPatternSuggestionsAnchorEl(e.currentTarget)}
                                                fullWidth
                                                multiline
                                                minRows={1}
                                                size="small"
                                            />
                                            <Popper
                                                open={Boolean(patternSuggestionsAnchorEl)}
                                                anchorEl={patternSuggestionsAnchorEl}
                                                placement="bottom-start"
                                                style={{ zIndex: 1400 }}
                                                modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
                                            >
                                                <Paper elevation={8} sx={{ maxHeight: 280, overflow: "auto", minWidth: 200 }}>
                                                    {mergedPatternSuggestions.length > 0 ? (
                                                        mergedPatternSuggestions.map((pattern, idx) => (
                                                            <MenuItem
                                                                key={idx}
                                                                dense
                                                                onClick={() => {
                                                                    const current = Array.isArray(localField.validation?.patterns)
                                                                        ? (localField.validation.patterns || []).slice()
                                                                        : [];
                                                                    const next = current.includes(pattern) ? current : [...current, pattern];
                                                                    setLocalField({
                                                                        ...localField,
                                                                        validation: {
                                                                            ...localField.validation,
                                                                            patterns: next.length ? next : null,
                                                                        },
                                                                    });
                                                                    setPatternSuggestionsAnchorEl(null);
                                                                }}
                                                                sx={{ fontFamily: "monospace", fontSize: "0.75rem", whiteSpace: "normal", wordBreak: "break-all" }}
                                                            >
                                                                {pattern}
                                                            </MenuItem>
                                                        ))
                                                    ) : (
                                                        <Box sx={{ px: 2, py: 1.5, color: "text.secondary", fontSize: "0.875rem" }}>
                                                            {localField?.type === "input"
                                                                ? "No suggested patterns. Check Percentage, Currency, or Time for format-specific suggestions."
                                                                : "No suggested patterns for this field type. Use types like datetime, email, or currency for regex suggestions."}
                                                        </Box>
                                                    )}
                                                </Paper>
                                            </Popper>
                                        </Box>
                                    </ClickAwayListener>
                                </Box>
                                <Autocomplete
                                    freeSolo
                                    options={validationErrorMessageSuggestions}
                                    value={localField.validationErrorMessage ?? ""}
                                    onChange={(_, value) =>
                                        setLocalField({
                                            ...localField,
                                            validationErrorMessage: value || null,
                                        })
                                    }
                                    onInputChange={(_, value) =>
                                        setLocalField({
                                            ...localField,
                                            validationErrorMessage: value || null,
                                        })
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Custom validation error message"
                                            size="small"
                                            sx={{ mt: 1 }}
                                            helperText="Shown when validation fails (e.g. when Required is checked this is often &quot;Required&quot;)"
                                        />
                                    )}
                                    sx={{ width: "100%", mt: 1 }}
                                />
                            </Box>
                        )}
                    </Box>
                )}

                {/* InlineInput: multiple content items with add/remove/reorder */}
                {mode !== "annotator" && localField.type === "inlineInput" && !isAnnotationImageBoxEditor && (() => {
                    const contentList = Array.isArray(localField.metadata?.content) && localField.metadata.content.length > 0
                        ? localField.metadata.content
                        : [{ attributeName: localField.attributeName || "value", placeholder: "", postLabel: "", preLabel: "", validation: {}, validationErrorMessage: null, value: null, width: "100px" }];
                    const updateContent = (nextContent) => setLocalField({
                        ...localField,
                        metadata: { ...localField.metadata, content: nextContent }
                    });
                    const updateItem = (index, patch) => {
                        const next = contentList.map((item, i) => i === index ? { ...item, ...patch } : item);
                        updateContent(next);
                    };
                    const addItem = () => updateContent([...contentList, { attributeName: "", placeholder: "", postLabel: "", preLabel: "", validation: {}, validationErrorMessage: null, value: null, width: "100px" }]);
                    const removeItem = (index) => updateContent(contentList.filter((_, i) => i !== index));
                    const moveItem = (index, delta) => {
                        const to = index + delta;
                        if (to < 0 || to >= contentList.length) return;
                        const next = [...contentList];
                        [next[index], next[to]] = [next[to], next[index]];
                        updateContent(next);
                    };
                    return (
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="subtitle1" color="primary" gutterBottom>
                                Inline Input (content items)
                            </Typography>
                            <Button size="small" variant="outlined" onClick={addItem} sx={{ mb: 2 }}>
                                Add content item
                            </Button>
                            {contentList.map((item, index) => (
                                <Box key={index} sx={{ mb: 3, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                        <Typography variant="subtitle2">Item {index + 1}</Typography>
                                        <Box>
                                            <IconButton size="small" onClick={() => moveItem(index, -1)} disabled={index === 0} aria-label="Move up">
                                                <ArrowUpwardIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => moveItem(index, 1)} disabled={index === contentList.length - 1} aria-label="Move down">
                                                <ArrowDownwardIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => removeItem(index)} disabled={contentList.length <= 1} color="error" aria-label="Remove">
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    <Stack spacing={2}>
                                        <TextField label="Attribute name" value={item.attributeName ?? ""} onChange={(e) => updateItem(index, { attributeName: e.target.value })} size="small" fullWidth />
                                        <TextField label="Pre label" value={item.preLabel ?? ""} onChange={(e) => updateItem(index, { preLabel: e.target.value })} size="small" fullWidth multiline />
                                        <TextField label="Post label" value={item.postLabel ?? ""} onChange={(e) => updateItem(index, { postLabel: e.target.value })} size="small" fullWidth multiline />
                                        <TextField label="Placeholder" value={item.placeholder ?? ""} onChange={(e) => updateItem(index, { placeholder: e.target.value })} size="small" fullWidth />
                                        <TextField label="Default value" value={item.value ?? ""} onChange={(e) => updateItem(index, { value: e.target.value || null })} size="small" fullWidth />
                                        <TextField label="Width" value={item.width ?? ""} onChange={(e) => updateItem(index, { width: e.target.value })} placeholder="e.g. 100px" size="small" fullWidth />
                                        <FormControlLabel
                                            control={<Checkbox checked={!!item.validation?.required} onChange={(e) => updateItem(index, { validation: { ...(item.validation || {}), required: e.target.checked } })} />}
                                            label="Required"
                                        />
                                        <TextField label="Validation error message" value={item.validationErrorMessage ?? ""} onChange={(e) => updateItem(index, { validationErrorMessage: e.target.value || null })} size="small" fullWidth />
                                        <TextField label="Input CSS class (fieldData.inputCssClass)" value={item.fieldData?.inputCssClass ?? ""} onChange={(e) => updateItem(index, { fieldData: { ...(item.fieldData || {}), inputCssClass: e.target.value || undefined } })} size="small" fullWidth placeholder="Optional CSS class" />
                                    </Stack>
                                    <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
                                        Preview: {item.preLabel ?? ""}{`{{${item.attributeName ?? ""}}}`}{item.postLabel ?? ""}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    );
                })()}

                {/* Options Section - Only show for option type fields */}
                {isOptionType && !isAnnotationImageBoxEditor && (
                    <Box sx={{ mt: 3 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                            <Typography variant="subtitle1">Options</Typography>
                            <Button onClick={handleAddOption} size="small" variant="outlined">
                                Add Option
                            </Button>
                        </Box>
                        {localOptions.map((option, index) => (
                            <Box
                                key={index}
                                sx={{
                                    mb: 2,
                                    p: 2,
                                    bgcolor: "background.paper",
                                    borderRadius: 1,
                                }}
                            >
                                {/* Existing option fields */}
                                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                                    {mode !== "annotator" && (
                                        <TextField
                                            label="Value"
                                            value={option.value}
                                            onChange={(e) =>
                                                handleOptionChange(index, "value", e.target.value)
                                            }
                                            size="small"
                                            error={!!optionErrors[index]}
                                            helperText={optionErrors[index]}
                                            sx={{ flexGrow: 1 }}
                                        />
                                    )}
                                    <TextField
                                        label={mode === "annotator" ? "Label" : "Text"}
                                        value={option.text}
                                        onChange={(e) =>
                                            handleOptionChange(index, "text", e.target.value)
                                        }
                                        size="small"
                                        sx={{ flexGrow: 1 }}
                                    />
                                </Box>

                                {/* Add Go To section for decision field options */}
                                {mode !== "annotator" && (localField.type === "decision" || localField.type === "decisionWithOther") && (
                                    <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                        <InputLabel>Go To Section</InputLabel>
                                        <Select
                                            value={option.goTo || ""}
                                            onChange={(e) =>
                                                handleOptionChange(index, "goTo", e.target.value)
                                            }
                                            label="Go To Section"
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            {getAvailableSections(localField.sectionId).map(
                                                (section) => (
                                                    <MenuItem
                                                        key={section.value}
                                                        value={section.value}
                                                    >
                                                        {section.value}
                                                    </MenuItem>
                                                )
                                            )}
                                        </Select>
                                    </FormControl>
                                )}

                                {/* Summary controls - same as before */}
                                {mode !== "annotator" && localField.type !== "decision" && localField.type !== "decisionWithOther" && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: 2,
                                            alignItems: "center",
                                            mt: 2,
                                        }}
                                    >
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <InputLabel>Deal Summary</InputLabel>
                                            <Select
                                                value={
                                                    option.dealSummary === undefined
                                                        ? "exclude"
                                                        : option.dealSummary
                                                            ? "show"
                                                            : "hide"
                                                }
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    handleOptionChange(
                                                        index,
                                                        "dealSummary",
                                                        value === "exclude" ? undefined : value === "show"
                                                    );
                                                }}
                                                label="Deal Summary"
                                            >
                                                <MenuItem value="exclude">Do not include</MenuItem>
                                                <MenuItem value="hide">Include (Hidden)</MenuItem>
                                                <MenuItem value="show">Include (Shown)</MenuItem>
                                            </Select>
                                        </FormControl>

                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <InputLabel>Form Summary</InputLabel>
                                            <Select
                                                value={
                                                    option.formSummary === undefined
                                                        ? "exclude"
                                                        : option.formSummary
                                                            ? "show"
                                                            : "hide"
                                                }
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    handleOptionChange(
                                                        index,
                                                        "formSummary",
                                                        value === "exclude" ? undefined : value === "show"
                                                    );
                                                }}
                                                label="Form Summary"
                                            >
                                                <MenuItem value="exclude">Do not include</MenuItem>
                                                <MenuItem value="hide">Include (Hidden)</MenuItem>
                                                <MenuItem value="show">Include (Shown)</MenuItem>
                                            </Select>
                                        </FormControl>

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={option.formUpload || false}
                                                    onChange={(e) => {
                                                        handleOptionChange(
                                                            index,
                                                            "formUpload",
                                                            e.target.checked
                                                        );
                                                    }}
                                                />
                                            }
                                            label="Include in Upload"
                                        />
                                    </Box>
                                )}
                                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                                    <IconButton
                                        onClick={() => handleRemoveOption(index)}
                                        color="error"
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Other Option Configuration */}
                {mode !== "annotator" && !isAnnotationImageBoxEditor && ["checkboxWithOther", "radioWithOther"].includes(localField.type) && (() => {
                    const checkboxOrRadioValue = localOther.checkbox?.value || localOther.radio?.value || "";
                    const expectedInputName = checkboxOrRadioValue ? `${checkboxOrRadioValue}Input` : "";
                    const currentInputName = localOther.input?.attributeName || "";
                    const patternMismatch = checkboxOrRadioValue && currentInputName !== expectedInputName;
                    
                    return (
                        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
                            <Typography variant="subtitle1" gutterBottom>
                                "Other" Option Configuration
                            </Typography>
                            
                            {/* Warning Banner if pattern doesn't match */}
                            {patternMismatch && (
                                <Alert 
                                    severity="warning" 
                                    sx={{ mb: 2 }}
                                    action={
                                        <Button
                                            color="inherit"
                                            size="small"
                                            onClick={() => {
                                                setLocalOther({
                                                    ...localOther,
                                                    input: {
                                                        ...localOther.input,
                                                        attributeName: expectedInputName
                                                    }
                                                });
                                            }}
                                        >
                                            Fix
                                        </Button>
                                    }
                                >
                                    The input attribute name should be <strong>"{expectedInputName}"</strong> to follow the standard (Option Value + "Input").
                                </Alert>
                            )}
                            
                            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                                <TextField
                                    label="Option Label"
                                    value={localOther.checkbox?.text || localOther.radio?.text || ""}
                                    onChange={(e) => {
                                        const key = localField.type === "radioWithOther" ? "radio" : "checkbox";
                                        setLocalOther({
                                            ...localOther,
                                            [key]: { ...localOther[key], text: e.target.value }
                                        });
                                    }}
                                    size="small"
                                    sx={{ flexGrow: 1 }}
                                    helperText="Label for the checkbox/radio"
                                />
                                <TextField
                                    label="Option Value"
                                    value={checkboxOrRadioValue}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        const expectedInputName = newValue ? `${newValue}Input` : "";
                                        const key = localField.type === "radioWithOther" ? "radio" : "checkbox";
                                        setLocalOther({
                                            ...localOther,
                                            [key]: { ...localOther[key], value: newValue },
                                            input: {
                                                ...localOther.input,
                                                attributeName: expectedInputName
                                            }
                                        });
                                    }}
                                    size="small"
                                    sx={{ flexGrow: 1 }}
                                    helperText="Saved value when selected (Input attribute name will be: {value}Input)"
                                />
                            </Box>
                            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                                {/* Input Attribute Name is auto-generated: {value}Input - Hidden but kept for programmatic access */}
                                <TextField
                                    label="Input Attribute Name"
                                    value={localOther.input?.attributeName || ""}
                                    onChange={(e) =>
                                        setLocalOther({
                                            ...localOther,
                                            input: { ...localOther.input, attributeName: e.target.value }
                                        })
                                    }
                                    error={!!otherInputError}
                                    helperText={otherInputError || "Where the text input value is saved"}
                                    size="small"
                                    sx={{ flexGrow: 1, display: 'none' }}
                                />
                                <TextField
                                    label="Input Placeholder"
                                    value={localOther.input?.placeholder || ""}
                                    onChange={(e) =>
                                        setLocalOther({
                                            ...localOther,
                                            input: { ...localOther.input, placeholder: e.target.value }
                                        })
                                    }
                                    size="small"
                                    sx={{ flexGrow: 1 }}
                                />
                            </Box>
                        </Box>
                    );
                })()}

                {/* Decision with Other: decisionValue + input */}
                {mode !== "annotator" && !isAnnotationImageBoxEditor && localField.type === "decisionWithOther" && (
                    <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Decision with Other
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                label="Other option value"
                                value={localOther.decisionValue ?? ""}
                                onChange={(e) => setLocalOther({ ...localOther, decisionValue: e.target.value })}
                                size="small"
                                fullWidth
                                helperText="Value stored when user selects the free-form other option"
                            />
                            <TextField
                                label="Input attribute name"
                                value={localOther.input?.attributeName ?? ""}
                                onChange={(e) => setLocalOther({ ...localOther, input: { ...localOther.input, attributeName: e.target.value } })}
                                size="small"
                                fullWidth
                                error={!!otherInputError}
                                helperText={otherInputError || "Attribute for the text input value"}
                            />
                            <TextField
                                label="Input placeholder"
                                value={localOther.input?.placeholder ?? ""}
                                onChange={(e) => setLocalOther({ ...localOther, input: { ...localOther.input, placeholder: e.target.value } })}
                                size="small"
                                fullWidth
                            />
                        </Stack>
                    </Box>
                )}

                {/* Decision / Decision with Other metadata */}
                {mode !== "annotator" && !isAnnotationImageBoxEditor && (localField.type === "decision" || localField.type === "decisionWithOther") && (
                    <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Decision metadata
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                label="Selected text"
                                value={localField.metadata?.selectedText ?? ""}
                                onChange={(e) => setLocalField({
                                    ...localField,
                                    metadata: { ...localField.metadata, selectedText: e.target.value || undefined }
                                })}
                                size="small"
                                fullWidth
                                helperText="metadata.selectedText"
                            />
                            <TextField
                                label="Clear value"
                                value={localField.metadata?.clearValue ?? ""}
                                onChange={(e) => setLocalField({
                                    ...localField,
                                    metadata: { ...localField.metadata, clearValue: e.target.value || undefined }
                                })}
                                size="small"
                                fullWidth
                                helperText="Option value for mutually-exclusive clear (e.g. None)"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={localField.metadata?.clausesEnabled === true}
                                        onChange={(e) => {
                                            // Checked -> metadata.clausesEnabled = true.
                                            // Unchecked -> remove the key entirely (empty
                                            // metadata is normalized to null on publish).
                                            // Decision fields hide the Additional Properties
                                            // section, so this is their Enable Clauses control.
                                            const nextMetadata = { ...(localField.metadata || {}) };
                                            if (e.target.checked) {
                                                nextMetadata.clausesEnabled = true;
                                            } else {
                                                delete nextMetadata.clausesEnabled;
                                            }
                                            setLocalField({ ...localField, metadata: nextMetadata });
                                        }}
                                    />
                                }
                                label="Enable Clauses"
                            />
                        </Stack>
                    </Box>
                )}

                {/* Datetime Field Properties */}
                {mode !== "annotator" && !isAnnotationImageBoxEditor && localField.type === "datetime" && (
                    <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Datetime Properties
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                label="Format"
                                value={localField.metadata?.fieldData?.format || "01/02/2006"}
                                onChange={(e) => {
                                    const format = e.target.value || "01/02/2006";
                                    setLocalField({
                                        ...localField,
                                        metadata: {
                                            ...localField.metadata,
                                            fieldData: {
                                                ...localField.metadata?.fieldData,
                                                format,
                                            },
                                        },
                                    });
                                }}
                                placeholder="e.g. 01/02/2006"
                                fullWidth
                                size="small"
                                helperText="Date display/parse format (e.g. 01/02/2006, MM/DD/YYYY)"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={localField.metadata?.mec !== undefined && localField.metadata?.mec !== null}
                                        onChange={(e) => {
                                            const updatedMetadata = {
                                                ...localField.metadata,
                                                fieldData: {
                                                    ...localField.metadata?.fieldData,
                                                    format: localField.metadata?.fieldData?.format || "01/02/2006"
                                                }
                                            };
                                            if (e.target.checked) {
                                                updatedMetadata.mec = 0;
                                            } else {
                                                // MEC off: clear both the offset and the mecOnly lock
                                                delete updatedMetadata.mec;
                                                delete updatedMetadata.mecOnly;
                                            }
                                            setLocalField({
                                                ...localField,
                                                metadata: updatedMetadata,
                                            });
                                        }}
                                    />
                                }
                                label="Enable Days After"
                            />
                            {localField.metadata?.mec !== undefined && localField.metadata?.mec !== null && (
                                <>
                                    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                                        <TextField
                                            label="Value (days offset)"
                                            type="number"
                                            value={localField.metadata?.mec ?? 0}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                const parsed = parseInt(raw, 10);
                                                setLocalField({
                                                    ...localField,
                                                    metadata: {
                                                        ...localField.metadata,
                                                        mec: raw === "" || Number.isNaN(parsed) ? 0 : parsed,
                                                    },
                                                });
                                            }}
                                            sx={{ flex: 1 }}
                                            size="small"
                                            helperText="0 = default. Use -1 when 0 days is a legitimate entry (lets the counter start at zero)."
                                        />
                                        {(() => {
                                            // Type options come from the types enabled in Form Settings (daysAfter).
                                            // MEC/BAD are the base type (no field flag); ABS/COE/DBC each set their
                                            // own mecIs* flag on this field's metadata.
                                            const da = daysAfterSettings || {};
                                            const meta = localField.metadata || {};
                                            // MEC/BAD share the base (no-flag) slot; whichever the form declares is the base label.
                                            const hasBase = !!(da.mec || da.bad);
                                            const baseValue = da.bad ? "bad" : "mec";
                                            const baseLabel = da.bad ? "BAD" : "MEC";
                                            const options = [];
                                            if (hasBase) options.push({ value: baseValue, label: baseLabel });
                                            if (da.bs) options.push({ value: "abs", label: "ABS" });
                                            if (da.coe) options.push({ value: "coe", label: "COE" });
                                            if (da.dbc) options.push({ value: "dbc", label: "DBC" });
                                            // Always keep the field's existing type selectable, even if Form Settings no longer lists it.
                                            if (meta.mecIsAbs && !options.some((o) => o.value === "abs")) options.push({ value: "abs", label: "ABS" });
                                            if (meta.mecIsCoe && !options.some((o) => o.value === "coe")) options.push({ value: "coe", label: "COE" });
                                            if (meta.mecIsDbc && !options.some((o) => o.value === "dbc")) options.push({ value: "dbc", label: "DBC" });
                                            if (options.length === 0) options.push({ value: "mec", label: "MEC" });
                                            // Current selection: an explicit flag wins; otherwise the base (no-add) when declared;
                                            // otherwise (only relative types declared) default to the single/first option.
                                            const flagType = meta.mecIsAbs ? "abs" : meta.mecIsCoe ? "coe" : meta.mecIsDbc ? "dbc" : null;
                                            const current = flagType ? flagType : hasBase ? baseValue : options[0].value;
                                            return (
                                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                                    <InputLabel id="days-after-type-label">Type</InputLabel>
                                                    <Select
                                                        labelId="days-after-type-label"
                                                        label="Type"
                                                        value={current}
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            const m = { ...localField.metadata };
                                                            // base (MEC/BAD) carries no flag; switching to a
                                                            // relative anchor sets exactly one mecIs* flag.
                                                            delete m.mecIsAbs;
                                                            delete m.mecIsCoe;
                                                            delete m.mecIsDbc;
                                                            if (v === "abs") m.mecIsAbs = true;
                                                            else if (v === "coe") m.mecIsCoe = true;
                                                            else if (v === "dbc") m.mecIsDbc = true;
                                                            setLocalField({ ...localField, metadata: m });
                                                        }}
                                                    >
                                                        {options.map((o) => (
                                                            <MenuItem key={o.value} value={o.value}>
                                                                {o.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            );
                                        })()}
                                    </Box>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={localField.metadata?.mecOnly === true}
                                                onChange={(e) => {
                                                    const updatedMetadata = { ...localField.metadata };
                                                    if (e.target.checked) {
                                                        updatedMetadata.mecOnly = true;
                                                    } else {
                                                        delete updatedMetadata.mecOnly;
                                                    }
                                                    setLocalField({
                                                        ...localField,
                                                        metadata: updatedMetadata,
                                                    });
                                                }}
                                            />
                                        }
                                        label="MEC only (disable date picker — number entry only)"
                                    />
                                </>
                            )}
                        </Stack>
                    </Box>
                )}

                {/* Summary, upload & offer compare (field-level) - JSON only */}
                {mode !== "annotator" && !isAnnotationImageBoxEditor && localField.type !== "decision" && localField.type !== "decisionWithOther" && (
                    <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Deal / Form Summary & Upload
                        </Typography>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            {/* Combined deadline rollup — component fields stay separate but
                                roll up into a single summary entry (combinedDate/combinedTime). */}
                            {(() => {
                                const kind = localField.combinedTime != null ? "time" : (localField.combinedDate != null ? "date" : "");
                                const enabled = kind !== "";
                                const keyVal = kind === "time" ? (localField.combinedTime ?? "") : (localField.combinedDate ?? "");
                                const labelVal = kind === "time" ? (localField.combinedTimeLabel ?? "") : (localField.combinedDateLabel ?? "");
                                // Existing rollups elsewhere in this form → reuse the same key/label/kind
                                // across component fields so they don't drift (e.g. closingDD → closingMonth).
                                const existingRollups = {};
                                for (const f of (fields || [])) {
                                    if (!f || f.id === localField.id) continue;
                                    if (f.combinedTime != null && f.combinedTime !== "") existingRollups[f.combinedTime] = { label: f.combinedTimeLabel ?? "", kind: "time" };
                                    else if (f.combinedDate != null && f.combinedDate !== "") existingRollups[f.combinedDate] = { label: f.combinedDateLabel ?? "", kind: "date" };
                                }
                                const rollupKeys = Object.keys(existingRollups);
                                const setRollup = (next) => {
                                    const { combinedDate, combinedTime, combinedDateLabel, combinedTimeLabel, ...rest } = localField;
                                    setLocalField({ ...rest, ...next });
                                };
                                const setPair = (k, v, l) => setRollup(k === "time"
                                    ? { combinedTime: v, combinedTimeLabel: l }
                                    : { combinedDate: v, combinedDateLabel: l });
                                return (
                                    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}>
                                        <FormControlLabel
                                            control={<Checkbox size="small" checked={enabled}
                                                onChange={(e) => e.target.checked ? setRollup({ combinedDate: "", combinedDateLabel: "" }) : setRollup({})} />}
                                            label="Roll up into a combined summary field"
                                        />
                                        {enabled && (
                                            <Stack spacing={1.5} sx={{ mt: 1 }}>
                                                <TextField select size="small" label="Rollup kind" value={kind}
                                                    onChange={(e) => setPair(e.target.value, keyVal, labelVal)} fullWidth>
                                                    <MenuItem value="date">Date</MenuItem>
                                                    <MenuItem value="time">Time</MenuItem>
                                                </TextField>
                                                <Autocomplete freeSolo size="small" fullWidth options={rollupKeys} value={keyVal}
                                                    onChange={(_e, v) => { const ex = existingRollups[v]; if (ex) setPair(ex.kind, v, ex.label); else setPair(kind, v || "", labelVal); }}
                                                    onInputChange={(_e, v, reason) => { if (reason === "input") setPair(kind, v, labelVal); }}
                                                    renderInput={(p) => <TextField {...p} label="Combined key (attributeName)" placeholder="e.g. acceptanceDeadlineDate"
                                                        helperText={rollupKeys.length ? "Pick an existing rollup to keep component fields consistent, or type a new key." : "Reuse the same key on each component field of this rollup."} />} />
                                                <TextField size="small" label="Combined label" placeholder="e.g. Acceptance Deadline"
                                                    value={labelVal} onChange={(e) => setPair(kind, keyVal, e.target.value)} fullWidth />
                                            </Stack>
                                        )}
                                    </Box>
                                );
                            })()}
                            <TextField
                                label="Form summary label override"
                                placeholder="Override label in formSummaryJSON"
                                value={localField.formSummaryLabel ?? ""}
                                disabled={localField.combinedDate != null || localField.combinedTime != null}
                                helperText={(localField.combinedDate != null || localField.combinedTime != null) ? "Superseded by the rollup label while this field is rolled up." : undefined}
                                onChange={(e) =>
                                    setLocalField({
                                        ...localField,
                                        formSummaryLabel: e.target.value || null,
                                    })
                                }
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label="Form summary type override"
                                placeholder="Override type in formSummaryJSON"
                                value={localField.formSummaryType ?? ""}
                                disabled={localField.combinedDate != null || localField.combinedTime != null}
                                onChange={(e) =>
                                    setLocalField({
                                        ...localField,
                                        formSummaryType: e.target.value || null,
                                    })
                                }
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label="Deal summary label override"
                                placeholder="Override label in dealSummaryJSON"
                                value={localField.dealSummaryLabel ?? ""}
                                disabled={localField.combinedDate != null || localField.combinedTime != null}
                                onChange={(e) =>
                                    setLocalField({
                                        ...localField,
                                        dealSummaryLabel: e.target.value || null,
                                    })
                                }
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label="Deal summary type override"
                                placeholder="Override type in dealSummaryJSON"
                                value={localField.dealSummaryType ?? ""}
                                disabled={localField.combinedDate != null || localField.combinedTime != null}
                                onChange={(e) =>
                                    setLocalField({
                                        ...localField,
                                        dealSummaryType: e.target.value || null,
                                    })
                                }
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label="Label (form upload context)"
                                placeholder="Custom label when used in form upload"
                                value={localField.labelFormUpload ?? ""}
                                onChange={(e) =>
                                    setLocalField({
                                        ...localField,
                                        labelFormUpload: e.target.value || null,
                                    })
                                }
                                fullWidth
                                size="small"
                            />
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Deal Summary</InputLabel>
                                <Select
                                    value={
                                        localField.dealSummary === undefined
                                            ? "exclude"
                                            : localField.dealSummary
                                                ? "show"
                                                : "hide"
                                    }
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setLocalField({
                                            ...localField,
                                            dealSummary: value === "exclude" ? undefined : value === "show",
                                        });
                                    }}
                                    label="Deal Summary"
                                >
                                    <MenuItem value="exclude">Do not include</MenuItem>
                                    <MenuItem value="hide">Include (Hidden)</MenuItem>
                                    <MenuItem value="show">Include (Shown)</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Form Summary</InputLabel>
                                <Select
                                    value={
                                        localField.formSummary === undefined
                                            ? "exclude"
                                            : localField.formSummary
                                                ? "show"
                                                : "hide"
                                    }
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setLocalField({
                                            ...localField,
                                            formSummary: value === "exclude" ? undefined : value === "show",
                                        });
                                    }}
                                    label="Form Summary"
                                >
                                    <MenuItem value="exclude">Do not include</MenuItem>
                                    <MenuItem value="hide">Include (Hidden)</MenuItem>
                                    <MenuItem value="show">Include (Shown)</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={localField.formUpload || false}
                                        onChange={(e) => {
                                            setLocalField({
                                                ...localField,
                                                formUpload: e.target.checked,
                                                metadata: {
                                                    ...localField.metadata,
                                                    formUploadData: e.target.checked ? localField.attributeName : undefined,
                                                },
                                            });
                                        }}
                                    />
                                }
                                label="Include in Form Upload"
                            />
                            {isPurchaseContract && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!localField.offerCompare}
                                            onChange={(e) => {
                                                setLocalField({
                                                    ...localField,
                                                    offerCompare: e.target.checked || undefined,
                                                });
                                            }}
                                        />
                                    }
                                    label="Include in Offer Compare"
                                />
                            )}
                        </Stack>
                    </Box>
                )}

                {/* Additional Field Properties */}
                {mode !== "annotator" && !isAnnotationImageBoxEditor && localField.type !== "decision" && localField.type !== "decisionWithOther" && (
                    <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Additional Properties
                        </Typography>
                        <Stack spacing={1}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={localField.readOnly || false}
                                        onChange={(e) =>
                                            setLocalField({
                                                ...localField,
                                                readOnly: e.target.checked,
                                            })
                                        }
                                    />
                                }
                                label="Read Only (pre-filled from deal data)"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={localField.customTemplateDoNotCopy || false}
                                        onChange={(e) =>
                                            setLocalField({
                                                ...localField,
                                                customTemplateDoNotCopy: e.target.checked,
                                            })
                                        }
                                    />
                                }
                                label="Custom Template (Do Not Copy)"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={localField.metadata?.clausesEnabled === true}
                                        onChange={(e) => {
                                            // Checked -> metadata.clausesEnabled = true.
                                            // Unchecked -> remove the key entirely (empty
                                            // metadata is normalized to null on publish).
                                            const nextMetadata = { ...(localField.metadata || {}) };
                                            if (e.target.checked) {
                                                nextMetadata.clausesEnabled = true;
                                            } else {
                                                delete nextMetadata.clausesEnabled;
                                            }
                                            setLocalField({ ...localField, metadata: nextMetadata });
                                        }}
                                    />
                                }
                                label="Enable Clauses"
                            />
                        </Stack>
                    </Box>
                )}

                {mode !== "annotator" &&
                    !isAnnotationImageBoxEditor &&
                    !localField.pdfEditorSyntheticKind &&
                    (
                    <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Also a signer field
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                            Publishes{" "}
                            <Box component="code" sx={{ fontSize: "0.8em" }}>
                                metadata.signerKey
                            </Box>{" "}
                            on this section field, tying its value to one signer (e.g. legal-info fields named
                            after the signer). Unchecked omits the key entirely.
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={alsoSignerField}
                                    onChange={(e) => {
                                        const on = e.target.checked;
                                        setAlsoSignerField(on);
                                        setLocalField((prev) => {
                                            const nextMetadata = { ...(prev.metadata || {}) };
                                            if (on) {
                                                // Prefill from the attribute-name prefix when it
                                                // matches a signer key; still fully overridable.
                                                const current =
                                                    typeof nextMetadata.signerKey === "string"
                                                        ? nextMetadata.signerKey.trim()
                                                        : "";
                                                const next = current || signerKeyFromAttributePrefix;
                                                if (next) nextMetadata.signerKey = next;
                                                else delete nextMetadata.signerKey;
                                            } else {
                                                delete nextMetadata.signerKey;
                                            }
                                            return { ...prev, metadata: nextMetadata };
                                        });
                                    }}
                                />
                            }
                            label="Is also a signer field"
                        />
                        {alsoSignerField && (
                            <Stack spacing={1} sx={{ mt: 1, pl: 0.5 }}>
                                {signerKeyOptions.length === 0 ? (
                                    <Alert severity="info">
                                        Add signers under Form Settings first, then pick which signer this field
                                        belongs to.
                                    </Alert>
                                ) : (
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="also-signer-key-label">Signer Key</InputLabel>
                                        <Select
                                            labelId="also-signer-key-label"
                                            label="Signer Key"
                                            value={
                                                typeof localField.metadata?.signerKey === "string"
                                                    ? localField.metadata.signerKey
                                                    : ""
                                            }
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setLocalField((prev) => {
                                                    const nextMetadata = { ...(prev.metadata || {}) };
                                                    if (value) nextMetadata.signerKey = value;
                                                    else delete nextMetadata.signerKey;
                                                    return { ...prev, metadata: nextMetadata };
                                                });
                                            }}
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            {signerKeyOptions.map((o) => (
                                                <MenuItem key={o.signerKey} value={o.signerKey}>
                                                    {o.signerKey} — {o.roleLabels.join(", ")}
                                                </MenuItem>
                                            ))}
                                            {/* Hand-authored key that no longer matches a signer */}
                                            {(() => {
                                                const cur =
                                                    typeof localField.metadata?.signerKey === "string"
                                                        ? localField.metadata.signerKey
                                                        : "";
                                                if (!cur || signerKeyOptions.some((o) => o.signerKey === cur)) {
                                                    return null;
                                                }
                                                return (
                                                    <MenuItem key={`orphan-${cur}`} value={cur}>
                                                        {cur} (not in form)
                                                    </MenuItem>
                                                );
                                            })()}
                                        </Select>
                                        <FormHelperText>
                                            {!signerKeyFromAttributePrefix
                                                ? "The attribute name has no signer key prefix — pick one."
                                                : localField.metadata?.signerKey === signerKeyFromAttributePrefix
                                                  ? `Matches the attribute name prefix (${signerKeyFromAttributePrefix}).`
                                                  : `The attribute name prefix suggests ${signerKeyFromAttributePrefix}.`}
                                        </FormHelperText>
                                    </FormControl>
                                )}
                            </Stack>
                        )}
                    </Box>
                )}

                {mode !== "annotator" && !isAnnotationImageBoxEditor && !localField.pdfEditorSyntheticKind && (
                    <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Editable after signing
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                            Publishes{" "}
                            <Box component="code" sx={{ fontSize: "0.8em" }}>
                                editableAnnotation.meviDealRoles
                            </Box>{" "}
                            on the field (deal roles matching Form Settings). Runtime may allow edits after those
                            signers have signed.
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={localField.editableAnnotation != null}
                                    onChange={(e) => {
                                        const on = e.target.checked;
                                        setLocalField((prev) => ({
                                            ...prev,
                                            editableAnnotation: on
                                                ? {
                                                      ...(prev.editableAnnotation && typeof prev.editableAnnotation === "object"
                                                          ? prev.editableAnnotation
                                                          : {}),
                                                      meviDealRoles: Array.isArray(prev.editableAnnotation?.meviDealRoles)
                                                          ? [...prev.editableAnnotation.meviDealRoles]
                                                          : [],
                                                  }
                                                : null,
                                        }));
                                    }}
                                />
                            }
                            label="Field can be edited after signing (per role)"
                        />
                        {localField.editableAnnotation != null && (
                            <Stack spacing={1} sx={{ mt: 1, pl: 0.5 }}>
                                {signingRoleKeysForEditable.length === 0 ? (
                                    <Alert severity="info">
                                        Add signer roles in Form Settings first, then choose which deal roles may edit this
                                        field after signing.
                                    </Alert>
                                ) : (
                                    <FormGroup>
                                        {signingRoleKeysForEditable.map((roleKey) => {
                                            const roleLabel =
                                                signatures[roleKey]?.label ??
                                                DEFAULT_SIGNER_ROLE_LABELS[roleKey] ??
                                                roleKey;
                                            const dealRole = dealRoleValueForSigningRole(roleKey);
                                            const arr = localField.editableAnnotation?.meviDealRoles;
                                            const list = Array.isArray(arr) ? arr : [];
                                            const checked =
                                                list.includes(dealRole) || list.includes(roleKey);
                                            return (
                                                <FormControlLabel
                                                    key={roleKey}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={checked}
                                                            onChange={(e) => {
                                                                const add = e.target.checked;
                                                                setLocalField((prev) => {
                                                                    const ea = prev.editableAnnotation;
                                                                    const base =
                                                                        ea && typeof ea === "object"
                                                                            ? { ...ea }
                                                                            : { meviDealRoles: [] };
                                                                    const prevList = Array.isArray(base.meviDealRoles)
                                                                        ? [...base.meviDealRoles]
                                                                        : [];
                                                                    const v = dealRoleValueForSigningRole(roleKey);
                                                                    let nextList;
                                                                    if (add) {
                                                                        nextList = prevList.includes(v)
                                                                            ? prevList
                                                                            : [...prevList, v];
                                                                    } else {
                                                                        nextList = prevList.filter(
                                                                            (x) => x !== v && x !== roleKey,
                                                                        );
                                                                    }
                                                                    return {
                                                                        ...prev,
                                                                        editableAnnotation: {
                                                                            ...base,
                                                                            meviDealRoles: nextList,
                                                                        },
                                                                    };
                                                                });
                                                            }}
                                                        />
                                                    }
                                                    label={`${roleLabel} — ${dealRole}`}
                                                />
                                            );
                                        })}
                                    </FormGroup>
                                )}
                            </Stack>
                        )}
                    </Box>
                )}

                {/* Extra PDF binding: section field box → additionalFieldsJSON or signaturesJSON (persona); JSON editors only */}
                {mode !== "annotator" &&
                    onFieldPdfBindingSync &&
                        !localField.pdfEditorSyntheticKind &&
                    effectiveExtraPdfBinding?.pdfLocation?.page != null && (
                        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Extra PDF output
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                Also publish a PDF box as a generic binding (same coordinates). For checkbox, radio, and
                                decision fields without a field-level box, the <strong>first option</strong> (or Other)
                                placement is used. Standard keeps the section field only; additional field and persona
                                data are not in formData. Signature and initials image boxes use{" "}
                                <strong>Add Signature</strong> / <strong>Add Initials</strong> on the PDF, or bind a
                                field box below.
                            </Typography>
                            <FormControl component="fieldset" variant="standard" sx={{ width: "100%" }}>
                                <FormLabel component="legend" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                                    Binding type
                                </FormLabel>
                                <RadioGroup
                                    value={extraPdfBindingKind}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setExtraPdfBindingKind(v);
                                        if (v === "personaData") {
                                            setPersonaPdfSubtype("attributePlacements");
                                        }
                                        if (v === "initialsData") {
                                            setPersonaPdfSubtype("signatureImage");
                                        }
                                    }}
                                >
                                    <FormControlLabel
                                        value="standard"
                                        control={<Radio size="small" />}
                                        label="Standard (section field only)"
                                    />
                                    <FormControlLabel
                                        value="additionalField"
                                        control={<Radio size="small" />}
                                        label="Additional field (not in formData)"
                                    />
                                    <FormControlLabel
                                        value="personaData"
                                        control={<Radio size="small" />}
                                        label="Signer field (not in formData, related to signers)"
                                    />
                                    <FormControlLabel
                                        value="initialsData"
                                        control={<Radio size="small" />}
                                        label="Initials data (not in formData, related to signers)"
                                    />
                                    <FormControlLabel
                                        value="subsequentSigning"
                                        control={<Radio size="small" />}
                                        label="Subsequent signing (not in formData)"
                                    />
                                </RadioGroup>
                            </FormControl>
                            {extraPdfBindingKind === "personaData" &&
                                personaPdfSubtype === "signatureImage" && (
                                    <Alert severity="info" sx={{ mt: 1 }}>
                                        This box has a linked <strong>signature image</strong> (from Add Signature).
                                        The signer below updates that placement. For signer <strong>text</strong>{" "}
                                        attributes, use Signer field after removing the signature link or use a separate
                                        field.
                                    </Alert>
                                )}
                            {extraPdfBindingKind === "initialsData" &&
                                personaPdfSubtype === "signatureImage" && (
                                    <Alert severity="info" sx={{ mt: 1 }}>
                                        This box is bound as an <strong>initials image</strong> (from Add Initials or
                                        linked here). For signer <strong>text</strong> attributes on initials rows,
                                        switch binding to attribute placements below.
                                    </Alert>
                                )}
                            {(extraPdfBindingKind === "personaData" || extraPdfBindingKind === "initialsData") && (
                                <Box sx={{ mt: 1.5 }}>
                                    {extraPdfSignerOptions.length > 0 ? (
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="extra-pdf-signer-label">
                                                {extraPdfBindingKind === "initialsData" ? "Initials Signer" : "Signer"}
                                            </InputLabel>
                                            <Select
                                                labelId="extra-pdf-signer-label"
                                                label={extraPdfBindingKind === "initialsData" ? "Initials Signer" : "Signer"}
                                                value={
                                                    extraPdfSignerOptions.some((o) => o.compound === extraPdfPersonaCompound)
                                                        ? extraPdfPersonaCompound
                                                        : extraPdfSignerOptions[0]?.compound || ""
                                                }
                                                onChange={(e) => setExtraPdfPersonaCompound(e.target.value)}
                                            >
                                                {extraPdfSignerOptions.map((o) => (
                                                    <MenuItem key={o.compound} value={o.compound}>
                                                        {o.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <Alert severity="warning" sx={{ mt: 1 }}>
                                            Add roles and signers under Form Settings to use persona binding.
                                        </Alert>
                                    )}
                                </Box>
                            )}
                            {extraPdfBindingKind === "initialsData" && (
                                <FormControl component="fieldset" variant="standard" sx={{ mt: 2, width: "100%" }}>
                                    <FormLabel component="legend" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                                        Initials output type
                                    </FormLabel>
                                    <RadioGroup
                                        value={personaPdfSubtype}
                                        onChange={(e) => setPersonaPdfSubtype(e.target.value)}
                                    >
                                        <FormControlLabel
                                            value="signatureImage"
                                            control={<Radio size="small" />}
                                            label="Initials image box (pdfLocation on initialsJSON signer row)"
                                        />
                                        <FormControlLabel
                                            value="attributePlacements"
                                            control={<Radio size="small" />}
                                            label="Signer attribute placements (initials persona / fields on row)"
                                        />
                                    </RadioGroup>
                                </FormControl>
                            )}
                            {extraPdfBindingKind === "subsequentSigning" && (
                                <Box sx={{ mt: 1.5 }}>
                                    {subsequentSignerOptionsFlat.length > 0 ? (
                                        <FormControl fullWidth size="small" variant="outlined">
                                            <InputLabel id="extra-pdf-subseq-label" shrink>
                                                Signer
                                            </InputLabel>
                                            <Select
                                                labelId="extra-pdf-subseq-label"
                                                label="Signer"
                                                value={extraPdfPersonaCompound || ""}
                                                onChange={(e) => setExtraPdfPersonaCompound(e.target.value)}
                                            >
                                                {subsequentSignerOptionsFlat.map((o) => (
                                                    <MenuItem key={o.compound} value={o.compound}>
                                                        {o.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <Alert severity="warning" sx={{ mt: 1 }}>
                                            Tick a role under Subsequent Signing Roles to use subsequent signing binding.
                                        </Alert>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1,
                        pt: 2,
                    }}
                >
                    <Button
                        onClick={() => {
                            const deleteTarget =
                                (field && field.id) ||
                                field ||
                                localField?.id ||
                                localField;
                            onDelete(deleteTarget);
                            onClose();
                        }}
                        color="error"
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                    >
                        {localField.pdfEditorSyntheticKind === "initialsAnnotation"
                            ? "Remove initials box"
                            : isAnnotationImageBoxEditor
                              ? "Remove signature box"
                              : "Delete Field"}
                    </Button>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            color="primary"
                            disabled={saveDisabled}
                        >
                            Save Changes
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </>
    );

    if (variant === "panel") {
        if (!open) return null;
        return (
            <Paper
                elevation={24}
                sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                {dialogBody}
            </Paper>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            {dialogBody}
        </Dialog>
    );
};

export default EditFieldDialog;
